import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from '../config/api';
import '../styles/Trades.css';
import InfoIcon from './InfoIcon';
import { getTooltipText } from '../config/tooltipTexts';
import AddPositionDialog from './AddPositionDialog';
import { formatTradeStatusCompact } from '../utils/tradeStatusUtils';
import { calculateOptimalHedgePrice, getOptimalDecimalPlaces as getOptimalDecimalPlacesForHedge } from '../utils/optimalHedgeCalculator';
import { buildTradingViewUrl } from '../utils/tradingViewUrl';

const TradeDetailWindow = ({ tradeId, refreshTrigger, onPositionCountChange, onWindowResize, onOpenHedgeCalculator }) => {
  // Configurable decimal places for display
  const DECIMAL_PLACES = 4;

  const [fundings, setFundings] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [positionJobs, setPositionJobs] = useState([]);
  const [tradeJobs, setTradeJobs] = useState([]);
  const [hedgeStrategies, setHedgeStrategies] = useState([]);
  const [positionToStrategy, setPositionToStrategy] = useState(new Map());

  // Ref for exchange-details-grid table
  const exchangeGridRef = useRef(null);
  const [columnMinWidths, setColumnMinWidths] = useState({ col2: 0, col3: 0 });
  const isUpdatingWidths = useRef(false);

  // Ref to always have latest tradeData in interval callbacks
  const tradeDataRef = useRef(null);
  const [jobDetailsPopup, setJobDetailsPopup] = useState({
    show: false,
    job: null,
    position: { x: 0, y: 0 }
  });
  const [showTradeJobDialog, setShowTradeJobDialog] = useState(false);
  const [tradeJobDialogData, setTradeJobDialogData] = useState({
    tradeId: '',
    actionType: 'OPEN-TRADE',
    triggerType: 'TRADE_PROFIT',
    condition: '>',
    tradeProfit: '',
    volume: '',
    scheduledTime: ''
  });
  const [showAddPositionDialog, setShowAddPositionDialog] = useState(false);

  // Helper function to check if value should be greyed out
  const getValueClass = (value) => {
    const strValue = String(value);
    return (strValue === 'N/A' || strValue === '---') ? 'detail-value na-value' : 'detail-value';
  };

  // Helper function to format numbers for display with configurable decimal places and thousand separators
  const formatForDisplay = (value, decimalPlaces = DECIMAL_PLACES) => {
    // Handle special values
    if (value === 'N/A' || value === '---' || value === null || value === undefined) {
      return value;
    }

    // Remove spaces from string values before parsing (e.g., "1 123.56" -> "1123.56")
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/\s/g, '')) : value;

    // Return original value if not a valid number
    if (isNaN(numValue)) {
      return value;
    }

    // Format to fixed decimal places
    const fixedValue = numValue.toFixed(decimalPlaces);

    // Split into integer and decimal parts
    const parts = fixedValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1] ? '.' + parts[1] : '';

    // Add thousand separators (spaces) to integer part
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return formatted + decimalPart;
  };

  // Helper function to format timestamps in YYYY-MM-DD HH:mm format
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '---';

    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Helper function to calculate optimal decimal places based on target digits
  const getOptimalDecimalPlaces = (targetDigits, myNumber, maxDecimalPlaces) => {
    // Convert to number if string
    const numValue = typeof myNumber === 'string' ? parseFloat(myNumber.replace(/\s/g, '')) : myNumber;

    // Handle invalid numbers
    if (isNaN(numValue) || numValue === null || numValue === undefined) {
      return maxDecimalPlaces;
    }

    // Get the integer part length
    const integerPart = Math.floor(Math.abs(numValue));
    const integerDigits = integerPart === 0 ? 1 : Math.floor(Math.log10(integerPart)) + 1;

    // Calculate available digits for decimal places
    // targetDigits - integerDigits - 1 (for decimal point)
    const availableForDecimals = targetDigits - integerDigits - 1;

    // Return the minimum of available space and max decimal places, but at least 0
    return Math.max(0, Math.min(availableForDecimals, maxDecimalPlaces));
  };

  // Calculate next funding time based on last funding time and period
  const getNextFundingTime = (fundingRateDateTime, fundingHoursPeriod) => {
    // Fallback to old hardcoded logic if no data available
    if (!fundingRateDateTime || !fundingHoursPeriod) {
      const now = new Date();
      const nextFunding = new Date(now);
      const currentHourUTC = now.getUTCHours();
      let nextFundingHour;
      if (currentHourUTC < 8) {
        nextFundingHour = 8;
      } else if (currentHourUTC < 16) {
        nextFundingHour = 16;
      } else {
        nextFundingHour = 0;
        nextFunding.setUTCDate(nextFunding.getUTCDate() + 1);
      }
      nextFunding.setUTCHours(nextFundingHour, 0, 0, 0);
      return nextFunding;
    }

    // Calculate based on actual funding data
    const now = new Date();
    const lastFundingTime = new Date(fundingRateDateTime);
    const periodHours = fundingHoursPeriod || 8; // Default to 8 hours if not provided

    // Calculate next funding time by adding period to last funding time
    let nextFundingTime = new Date(lastFundingTime);

    // Keep adding periods until we find a time in the future
    while (nextFundingTime <= now) {
      nextFundingTime = new Date(nextFundingTime.getTime() + periodHours * 60 * 60 * 1000);
    }

    return nextFundingTime;
  };

  // Format next funding time for display
  const formatNextFundingTime = (date) => {
    if (!date) return '-';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Format countdown
  const formatCountdown = (futureDate) => {
    if (!futureDate) return { text: '--:--:--', color: '#8b949e' };
    const now = currentTime;
    const diff = futureDate - now;
    if (diff <= 0) return { text: '00:00:00', color: '#8b949e' };
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    const totalMinutes = hours * 60 + minutes;
    let color;
    if (totalMinutes >= 60) {
      color = '#3fb950';
    } else if (totalMinutes >= 30) {
      color = '#ff9800';
    } else {
      color = '#ff6b6b';
    }
    return {
      text: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      color: color
    };
  };

  const [tradeData, setTradeData] = useState({
    tradeId: '---',
    tradeStatus: '---',
    realizedProfitPercent: '---',
    realizedProfitUsdt: '---',
    maxAllocatedCapital: null,
    positions: [] // All positions for this trade
  });

  // Store symbolId for periodic orderbook fetching
  const [symbolId, setSymbolId] = useState(null);

  // Volume input states for each position (indexed by position index)
  const [volumes, setVolumes] = useState({});

  // Error states for API calls (indexed by position index)
  const [orderbookErrors, setOrderbookErrors] = useState({});
  const [dtmErrors, setDtmErrors] = useState({});
  const [orderbookStats, setOrderbookStats] = useState({});
  const [showOrderbookStats, setShowOrderbookStats] = useState(true);

  // Job dialog state
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [jobDialogData, setJobDialogData] = useState({
    positionId: '',
    exchange: '',
    actionType: 'OPEN',
    triggerType: 'funding_rate',
    condition: '>',
    volume: '',
    scheduledTime: '',
    conditionValue: ''
  });

  useEffect(() => {
    if (tradeId) {
      loadTradeDetails(tradeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeId, refreshTrigger]);

  // Keep ref updated with latest tradeData
  useEffect(() => {
    tradeDataRef.current = tradeData;
  }, [tradeData]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Periodic orderbook fetching effect for all positions
  useEffect(() => {
    if (!tradeData.positions || tradeData.positions.length === 0 || !symbolId) {
      return; // Don't fetch if we don't have positions data
    }

    // Function to fetch orderbook for all positions
    const fetchOrderbooks = async () => {
      try {
        // Use ref to get latest position data
        const currentPositions = tradeDataRef.current.positions;
        if (!currentPositions || currentPositions.length === 0) {
          return;
        }

        // Group positions by unique (exchangeId, symbolId, market) combinations
        const combinationMap = new Map();

        currentPositions.forEach((pos, index) => {
          const contract = pos.contract.toUpperCase();
          const market = contract === 'SPOT' ? 'spot' : 'perpetual';
          const key = `${pos.exchangeId}-${symbolId}-${market}`;

          if (!combinationMap.has(key)) {
            combinationMap.set(key, {
              exchangeId: pos.exchangeId,
              symbolId: symbolId,
              market: market,
              positionIndices: []
            });
          }

          combinationMap.get(key).positionIndices.push(index);
        });

        // Create an array to collect all updates
        const positionUpdates = [];

        // Fetch orderbook and DTM once per unique combination
        for (const [key, combo] of combinationMap.entries()) {
          let orderbookData = null;
          let dtmData = null;

          // Fetch orderbook
          try {
            const orderbookResponse = await fetch(
              `${API_BASE_URL}/api/orderbook?exchangeId=${combo.exchangeId}&symbolId=${combo.symbolId}&limit=5&market=${combo.market}`
            );
            orderbookData = await orderbookResponse.json();

            if (orderbookData.success) {
              // Clear errors for all positions using this combination
              combo.positionIndices.forEach(i => {
                setOrderbookErrors(prev => ({ ...prev, [i]: null }));
              });
            } else {
              // Set error for all positions using this combination
              const errorMsg = orderbookData.message || orderbookData.error || 'Failed to fetch orderbook';
              combo.positionIndices.forEach(i => {
                setOrderbookErrors(prev => ({ ...prev, [i]: errorMsg }));
              });
            }
          } catch (error) {
            console.error(`Error fetching orderbook for combination ${key}:`, error);
            const errorMsg = error.message || 'Failed to fetch orderbook';
            combo.positionIndices.forEach(i => {
              setOrderbookErrors(prev => ({ ...prev, [i]: errorMsg }));
            });
          }

          // Fetch DTM
          try {
            const dtmResponse = await fetch(
              `${API_BASE_URL}/api/orderbook/depth-to-move?exchangeId=${combo.exchangeId}&symbolId=${combo.symbolId}&market=${combo.market}`
            );
            dtmData = await dtmResponse.json();

            if (dtmData.success) {
              combo.positionIndices.forEach(i => {
                setDtmErrors(prev => ({ ...prev, [i]: null }));
              });
            } else {
              const errorMsg = dtmData.message || dtmData.error || 'Failed to fetch DTM';
              combo.positionIndices.forEach(i => {
                setDtmErrors(prev => ({ ...prev, [i]: errorMsg }));
              });
            }
          } catch (error) {
            console.error(`Error fetching DTM for combination ${key}:`, error);
            const errorMsg = error.message || 'Failed to fetch DTM';
            combo.positionIndices.forEach(i => {
              setDtmErrors(prev => ({ ...prev, [i]: errorMsg }));
            });
          }

          // Fetch orderbook stats (trade volume per time window)
          try {
            const firstPos = currentPositions[combo.positionIndices[0]];
            const baseSymbol = firstPos.symbol.toUpperCase();
            const statsSymbol = baseSymbol.endsWith('USDT') ? baseSymbol : baseSymbol + 'USDT';
            const statsResponse = await fetch(
              `${API_BASE_URL}/api/orderbook-stats?exchange=${encodeURIComponent(firstPos.name)}&contractType=${combo.market}&symbol=${encodeURIComponent(statsSymbol)}`
            );
            const statsData = await statsResponse.json();
            if (statsData.success) {
              combo.positionIndices.forEach(i => {
                setOrderbookStats(prev => ({ ...prev, [i]: statsData.windows }));
              });
            }
          } catch (error) {
            console.error(`Error fetching orderbook stats for combination ${key}:`, error);
          }

          // Process all positions that share this combination
          if (orderbookData && orderbookData.success) {
            const bestBid = orderbookData.data.bestBid.price;
            const actualPrice = orderbookData.data.actualPrice;
            const bestAsk = orderbookData.data.bestAsk.price;

            for (const i of combo.positionIndices) {
              const pos = currentPositions[i];
              const contract = pos.contract.toUpperCase();

              const takerFeePercent = parseFloat(pos.takerFee) || 0;
              const makerFeePercent = parseFloat(pos.makerFee) || 0;
              const takerFeeDecimal = takerFeePercent / 100;
              const makerFeeDecimal = makerFeePercent / 100;
              const takerPrice = bestAsk * (1 + takerFeeDecimal);
              const makerPrice = actualPrice * (1 + makerFeeDecimal);

              // Calculate PNL for open positions
              let actualPnl = pos.position.actualPnl;
              let actualPnlUsdt = pos.position.actualPnlUsdt;
              let bestPricePnl = pos.position.bestPricePnl;
              let bestPricePnlUsdt = pos.position.bestPricePnlUsdt;

              if (pos.position.status === 'closed') {
                // For closed positions: keep existing PNL
              } else if (pos.position.status === 'open' && pos.position.openingPriceRaw) {
                // For open positions: use same formula as closed positions
                const openingPrice = parseFloat(pos.position.openingPriceRaw);
                const openingVolume = parseFloat(pos.position.openingVolumeRaw || 1);
                const openingFee = parseFloat(pos.position.openingFee) || 0;

                // Get taker fee for closing (as decimal)
                const takerFeePercent = parseFloat(pos.takerFee) || 0;
                const takerFeeDecimal = takerFeePercent / 100;

                // Calculate paid for position:
                // LONG/SPOT (buying to open): pay price + fee
                // SHORT (selling to open): receive price - fee
                const paidOrGainForPosition = contract === 'SHORT'
                  ? (openingPrice - openingFee) * openingVolume
                  : (openingPrice + openingFee) * openingVolume;

                // PNL from actual price:
                // LONG/SPOT (selling to close): receive price - fee
                // SHORT (buying to close): pay price + fee
                const closedPositionFor = contract === 'SHORT'
                  ? bestAsk * (1 + takerFeeDecimal) * openingVolume
                  : bestBid * (1 - takerFeeDecimal) * openingVolume;

                // console.error(`closedPositionFor(${closedPositionFor}) = actualPrice(${actualPrice}) * (1 - takerFeeDecimal) * openingVolume(${openingVolume})    paidOrGainForPosition = ${paidOrGainForPosition}`,closedPositionFor, actualPrice, openingVolume, paidOrGainForPosition);
                let pnlUsdt = closedPositionFor - paidOrGainForPosition;
                if (contract === 'SHORT') {
                  pnlUsdt = pnlUsdt * (-1);
                }
                actualPnlUsdt = pnlUsdt;
                actualPnl = (pnlUsdt / paidOrGainForPosition) * 100;

                // PNL from best bid/ask (best bid for LONG and SPOT, best ask for SHORT)
                const bestPrice = contract === 'SHORT' ? bestAsk : bestBid;
                const bestClosedPositionFor = bestPrice * (1 - takerFeeDecimal) * openingVolume;

                let bestPnlUsdt = bestClosedPositionFor - paidOrGainForPosition;
                if (contract === 'SHORT') {
                  bestPnlUsdt = bestPnlUsdt * (-1);
                }
                bestPricePnlUsdt = bestPnlUsdt;
                bestPricePnl = (bestPnlUsdt / paidOrGainForPosition) * 100;
              }

              positionUpdates.push({
                index: i,
                orderBook: {
                  bestSell: bestBid,
                  actualPrice: actualPrice,
                  bestAsk: bestAsk,
                  takerPrice: takerPrice,
                  makerPrice: makerPrice,
                  depthToMove: dtmData && dtmData.success ? dtmData.data.depth.price.toFixed(2) : pos.orderBook.depthToMove,
                  depthToMoveVolume: dtmData && dtmData.success ? dtmData.data.depth.quantity.toFixed(2) : pos.orderBook.depthToMoveVolume
                },
                position: {
                  ...pos.position,
                  actualPnl: actualPnl,
                  actualPnlUsdt: actualPnlUsdt,
                  bestPricePnl: bestPricePnl,
                  bestPricePnlUsdt: bestPricePnlUsdt
                }
              });
            }
          }
        }

        // Apply all updates at once
        if (positionUpdates.length > 0) {
          setTradeData(prev => {
            const updatedPositions = [...prev.positions];

            positionUpdates.forEach(update => {
              const existingPosition = updatedPositions[update.index];
              const isClosed = existingPosition?.position?.status === 'closed';

              updatedPositions[update.index] = {
                ...updatedPositions[update.index],
                orderBook: update.orderBook,
                position: {
                  // Preserve all existing position data to prevent race condition overwrites
                  ...existingPosition?.position,
                  // Only update PNL fields for OPEN positions (not for closed positions)
                  // Closed positions have fixed PNL calculated from opening/closing values, not market data
                  ...(isClosed ? {} : {
                    actualPnl: update.position.actualPnl,
                    actualPnlUsdt: update.position.actualPnlUsdt,
                    bestPricePnl: update.position.bestPricePnl,
                    bestPricePnlUsdt: update.position.bestPricePnlUsdt
                  })
                }
              };
            });

            return {
              ...prev,
              positions: updatedPositions
            };
          });
        }
      } catch (error) {
        console.error('Error fetching orderbooks:', error);
      }
    };

    // Fetch immediately
    fetchOrderbooks();

    // Set up periodic fetching every 3 seconds
    const intervalId = setInterval(fetchOrderbooks, 3000);

    // Cleanup interval on unmount or when dependencies change
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeData.positions.length, symbolId]);

  // Monitor column widths and set min-width to prevent sh  rinking
  useEffect(() => {
    const tableElement = exchangeGridRef.current;
    if (!tableElement) return;

    let rafId = null;
    const THRESHOLD = 2; // Only update if width increases by more than 2px

    const resizeObserver = new ResizeObserver(() => {
      // Skip if we're currently updating widths to prevent loops
      if (isUpdatingWidths.current) return;

      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      // Use requestAnimationFrame to batch DOM updates
      rafId = requestAnimationFrame(() => {
        // Get all rows in the table
        const rows = tableElement.querySelectorAll('tr.exchange-row');
        if (rows.length === 0) return;

        // Calculate max width for column 2 and column 3
        let maxWidth2 = columnMinWidths.col2;
        let maxWidth3 = columnMinWidths.col3;

        rows.forEach(row => {
          const cells = row.querySelectorAll('td.exchange-cell');
          if (cells.length >= 3) {
            const cell2 = cells[1]; // Second column (Exchange 1)
            const cell3 = cells[2]; // Third column (Exchange 2)

            const width2 = cell2.offsetWidth;
            const width3 = cell3.offsetWidth;

            if (width2 > maxWidth2) maxWidth2 = width2;
            if (width3 > maxWidth3) maxWidth3 = width3;
          }
        });

        // Only update if the increase is significant (above threshold)
        const shouldUpdate =
          (maxWidth2 - columnMinWidths.col2 > THRESHOLD) ||
          (maxWidth3 - columnMinWidths.col3 > THRESHOLD);

        if (shouldUpdate) {
          setColumnMinWidths({ col2: maxWidth2, col3: maxWidth3 });
        }
      });
    });

    // Observe the table element itself rather than individual cells
    resizeObserver.observe(tableElement);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, [columnMinWidths]);

  // Separate effect to apply min-width styles
  useEffect(() => {
    const tableElement = exchangeGridRef.current;
    if (!tableElement) return;

    // Set flag to prevent measuring while updating
    isUpdatingWidths.current = true;

    const rows = tableElement.querySelectorAll('tr.exchange-row');
    rows.forEach(row => {
      const cells = row.querySelectorAll('td.exchange-cell');
      if (cells.length >= 3) {
        if (columnMinWidths.col2 > 0) {
          cells[1].style.minWidth = `${columnMinWidths.col2}px`;
        }
        if (columnMinWidths.col3 > 0) {
          cells[2].style.minWidth = `${columnMinWidths.col3}px`;
        }
      }
    });

    // Reset flag after a short delay to allow DOM to settle
    setTimeout(() => {
      isUpdatingWidths.current = false;
    }, 100);
  }, [columnMinWidths]);

  // Measure table width and notify parent to resize window
  useEffect(() => {
    const tableElement = exchangeGridRef.current;
    if (!tableElement || !onWindowResize) return;

    // Wait for table to render and stabilize
    const timeoutId = setTimeout(() => {
      const tableWidth = tableElement.offsetWidth;
      // Add padding (16px left + 100px right = 116px total)
      const totalWidth = tableWidth + 27;
      onWindowResize(totalWidth);
    }, 300); // Wait 300ms for table to fully render with all columns

    return () => clearTimeout(timeoutId);
  }, [tradeData.positions.length, onWindowResize]);

  // Calculate the sum of realized fundings for each position
  const calculateRealizedFundings = (fundingsData) => {
    setTradeData(prev => {
      if (!prev.positions || prev.positions.length < 2) {
        return prev; // Cannot calculate fundings without at least 2 positions
      }

      const updatedPositions = prev.positions.map((pos, idx) => {
        const contract = pos.contract.toUpperCase();
        // Only show fundings for LONG or SHORT contracts (store raw values)
        if (contract !== 'LONG' && contract !== 'SHORT') {
          return {
            ...pos,
            position: { ...pos.position, actualRealizedFundings: 'N/A' }
          };
        }
        const key = `ex${idx + 1}_funding`;
        let total = 0;
        fundingsData.forEach(funding => {
          if (funding[key] !== null && funding[key] !== undefined) {
            total += parseFloat(funding[key]);
          }
        });
        return {
          ...pos,
          position: { ...pos.position, actualRealizedFundings: total }
        };
      });

      return { ...prev, positions: updatedPositions };
    });
  };

  const loadTradeDetails = async (tradeId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`);
      const data = await response.json();

      if (data.success) {
        const trade = data.data;

        // Helper function to process a single position
        const processPosition = async (pos) => {
          const posContract = (pos.contract_type || '').toUpperCase();

            // Fetch fees for this position
            let posFundingFee = '---';
            let posTakerFee = '---';
            let posMakerFee = '---';
            let posFundingRateDateTime = null;
            let posFundingHoursPeriod = null;

            if (posContract === 'SPOT') {
              posFundingFee = 'N/A';
              try {
                const feesUrl = `${API_BASE_URL}/api/taker-maker-fees?exchangeId=${pos.exchange_id}&symbolId=${pos.symbol_id}`;
                const feesResponse = await fetch(feesUrl);
                const feesData = await feesResponse.json();
                if (feesData.success && feesData.data.length > 0) {
                  const spotFee = feesData.data.find(f => f.category && f.category.toLowerCase() === 'spot');
                  if (spotFee) {
                    posTakerFee = spotFee.taker_fee != null ? (spotFee.taker_fee * 100).toFixed(4) : '---';
                    posMakerFee = spotFee.maker_fee != null ? (spotFee.maker_fee * 100).toFixed(4) : '---';
                  }
                }
              } catch (error) {
                console.error(`Error fetching spot fees for position ${pos.position_id}:`, error);
              }
            } else if (posContract === 'LONG' || posContract === 'SHORT') {
              try {
                const fundingResponse = await fetch(`${API_BASE_URL}/api/funding-fees?exchangeId=${pos.exchange_id}&symbolId=${pos.symbol_id}`);
                const fundingData = await fundingResponse.json();
                if (fundingData.success && fundingData.data.length > 0) {
                  const fundingInfo = fundingData.data[0];
                  const rate = fundingInfo.funding_rate;
                  posFundingFee = (rate != null) ? (rate * 100).toFixed(4) : '---';
                  posFundingRateDateTime = fundingInfo.funding_rate_date_time;
                  posFundingHoursPeriod = fundingInfo.funding_hours_period;
                }
              } catch (error) {
                console.error(`Error fetching funding fee for position ${pos.position_id}:`, error);
              }

              try {
                const feesUrl = `${API_BASE_URL}/api/taker-maker-fees?exchangeId=${pos.exchange_id}&symbolId=${pos.symbol_id}`;
                const feesResponse = await fetch(feesUrl);
                const feesData = await feesResponse.json();
                if (feesData.success && feesData.data.length > 0) {
                  const futuresFee = feesData.data.find(f =>
                    f.category && (f.category.toLowerCase() === 'futures' || f.category.toLowerCase() === 'perpetual' || f.category.toLowerCase() === 'inverse')
                  );
                  if (futuresFee) {
                    posTakerFee = futuresFee.taker_fee != null ? (futuresFee.taker_fee * 100).toFixed(4) : '---';
                    posMakerFee = futuresFee.maker_fee != null ? (futuresFee.maker_fee * 100).toFixed(4) : '---';
                  }
                }
              } catch (error) {
                console.error(`Error fetching futures fees for position ${pos.position_id}:`, error);
              }
            }

            const posIsOpenOrClosed = (pos.status === 'open' || pos.status === 'closed');
            const posOpenedAt = posIsOpenOrClosed && pos.realized_opening_price != null
              ? formatForDisplay(parseFloat(pos.realized_opening_price), 6)
              : '---';
            const posOpeningTimestamp = posIsOpenOrClosed && pos.realized_opening_timestamp
              ? new Date(pos.realized_opening_timestamp).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
              : '---';
            const posOpeningFee = pos.realized_opening_fee != null ? pos.realized_opening_fee : '---';
            const posOpeningFeePercent = posIsOpenOrClosed && pos.realized_opening_fee != null && pos.realized_opening_price != null
              ? (parseFloat(pos.realized_opening_fee / pos.realized_opening_price) * 100).toFixed(4)
              : '---';
            const posOpeningPaidFeeUsdt = posIsOpenOrClosed && pos.realized_opening_price != null && pos.realized_opening_volume != null && pos.realized_opening_fee != null
              ? formatForDisplay((parseFloat(pos.realized_opening_volume) * parseFloat(pos.realized_opening_fee)).toFixed(4))
              : '---';
            const posPaidOrGainForPosition = posIsOpenOrClosed && pos.realized_opening_price != null && pos.realized_opening_fee != null && pos.realized_opening_volume != null
              ? (posContract === 'LONG' || posContract === 'SPOT'
                  ? formatForDisplay((   (parseFloat(pos.realized_opening_price) + parseFloat(pos.realized_opening_fee)) * parseFloat(pos.realized_opening_volume)).toFixed(4))
                  : formatForDisplay((   (parseFloat(pos.realized_opening_price) - parseFloat(pos.realized_opening_fee)) * parseFloat(pos.realized_opening_volume)).toFixed(4)))
              : '---';
            const posClosedAt = (pos.status === 'closed' && pos.realized_closing_price != null)
              ? formatForDisplay(parseFloat(pos.realized_closing_price), 6)
              : '---';
            const posClosingTimestamp = (pos.status === 'closed' && pos.realized_closing_timestamp)
              ? new Date(pos.realized_closing_timestamp).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
              : '---';
            const posClosingFeePercent = (pos.status === 'closed' && pos.realized_closing_fee != null && pos.realized_closing_price != null)
              ? (parseFloat(pos.realized_closing_fee)/ parseFloat(pos.realized_closing_price)* 100).toFixed(4)
              : '---';
            const posClosingPaidFeeUsdt = (pos.status === 'closed' && pos.realized_closing_price != null && pos.realized_opening_volume != null && pos.realized_closing_fee != null)
              ? formatForDisplay((parseFloat(pos.realized_opening_volume) * parseFloat(pos.realized_closing_fee)).toFixed(4))
              : '---';
            const posClosedPaidOrGainPosition = (pos.status === 'closed' && pos.realized_closing_price != null && pos.realized_opening_volume != null && pos.realized_closing_fee != null)
              ? (posContract === 'LONG' || posContract === 'SPOT'
                  ? formatForDisplay((parseFloat(pos.realized_closing_price)-parseFloat(pos.realized_closing_fee))* parseFloat(pos.realized_opening_volume))
                  : formatForDisplay((parseFloat(pos.realized_closing_price)+parseFloat(pos.realized_closing_fee))* parseFloat(pos.realized_opening_volume)))
              : '---';

            let posActualPnlUsdt = '---';
            let posActualPnl = '---';

            // Initialize best price PNL
            let posBestPricePnl = '---';
            let posBestPricePnlUsdt = '---';

            if (pos.status === 'closed' && posPaidOrGainForPosition !== '---' && posClosedPaidOrGainPosition !== '---') {
              // For closed positions: calculate final PNL from opening and closing values
              const paidOrGainForPositionNum = parseFloat(posPaidOrGainForPosition.replace(/\s/g, ''));
              const closedPositionForNum = parseFloat(posClosedPaidOrGainPosition.replace(/\s/g, ''));
              posActualPnlUsdt = parseFloat((closedPositionForNum - paidOrGainForPositionNum));
              posActualPnl = parseFloat(((closedPositionForNum - paidOrGainForPositionNum) / paidOrGainForPositionNum * 100).toFixed(4));
              if (posContract === 'SHORT') {
                posActualPnlUsdt = posActualPnlUsdt * (-1);
                posActualPnl = posActualPnl * (-1);
              }
              // For closed positions, set bracketed PNL same as actual PNL (already finalized)
              posBestPricePnlUsdt = posActualPnlUsdt;
              posBestPricePnl = posActualPnl;
            } else if (pos.status === 'open') {
              // For open positions: initialize PNL to 0 (will be updated by orderbook effect)
              posActualPnlUsdt = 0;
              posActualPnl = 0;
              posBestPricePnl = 0;
              posBestPricePnlUsdt = 0;
            }

          return {
            name: pos.exchange_name || '---',
            symbol: pos.symbol || '---',
            contract: pos.contract_type || '---',
            positionId: pos.position_id || '---',
            exchangeId: pos.exchange_id,
            symbolId: pos.symbol_id,
            fundingFee: posFundingFee,
            takerFee: posTakerFee,
            makerFee: posMakerFee,
            fundingRateDateTime: posFundingRateDateTime,
            fundingHoursPeriod: posFundingHoursPeriod,
            orderBook: {
              bestSell: '---',
              actualPrice: '---',
              bestBuy: '---',
              takerPrice: '---',
              makerPrice: '---',
              depthToMove: '---',
              depthToMoveVolume: '---'
            },
            position: {
              status: pos.status || 'pending',
              opened: posIsOpenOrClosed,
              openedAt: posOpenedAt,
              openingTimestamp: posOpeningTimestamp,
              actualPrice: pos.realized_opening_price || '---',
              openingPriceRaw: pos.realized_opening_price,
              openingVolumeRaw: pos.realized_opening_volume,
              openingFee: posOpeningFee,
              openingFeePercent: posOpeningFeePercent,
              openingPaidFeeUsdt: posOpeningPaidFeeUsdt,
              paidOrGainForPosition: posPaidOrGainForPosition,
              closedAt: posClosedAt,
              closingTimestamp: posClosingTimestamp,
              closingFee: pos.realized_closing_fee || '---',
              closingFeePercent: posClosingFeePercent,
              closingPaidFeeUsdt: posClosingPaidFeeUsdt,
              closedPositionFor: posClosedPaidOrGainPosition,
              actualPnlUsdt: posActualPnlUsdt,
              actualPnl: posActualPnl,
              bestPricePnl: posBestPricePnl,
              bestPricePnlUsdt: posBestPricePnlUsdt,
              actualRealizedFundings: '---',
              hedgePositionOrder: pos.trade_hedge_position_order || 0
            }
          };
        };

        // Process ALL positions (including first two)
        const allPositions = [];
        if (trade.positions && trade.positions.length > 0) {
          for (const pos of trade.positions) {
            const processedPosition = await processPosition(pos);
            allPositions.push(processedPosition);
          }
        }

        // Calculate max simultaneously allocated capital using a timeline sweep.
        // For each position that was ever opened, add a +capital event at its opening
        // timestamp and a -capital event at its closing timestamp (omitted if still open).
        // The peak running sum is the maximum capital in use at any one point in time.
        const maxAllocatedCapital = (() => {
          if (!trade.positions || trade.positions.length === 0) return null;
          const events = [];
          for (const pos of trade.positions) {
            if (pos.status === 'pending') continue;
            const openingPrice = parseFloat(pos.realized_opening_price);
            const openingFee = parseFloat(pos.realized_opening_fee);
            const openingVolume = parseFloat(pos.realized_opening_volume);
            if (isNaN(openingPrice) || isNaN(openingFee) || isNaN(openingVolume)) continue;
            const capital = (openingPrice + openingFee) * openingVolume;
            const openTime = pos.realized_opening_timestamp ? new Date(pos.realized_opening_timestamp).getTime() : null;
            const closeTime = pos.realized_closing_timestamp ? new Date(pos.realized_closing_timestamp).getTime() : null;
            if (openTime === null) continue;
            events.push({ time: openTime, delta: capital });
            if (closeTime !== null) events.push({ time: closeTime, delta: -capital });
          }
          if (events.length === 0) return null;
          // At equal timestamps, process closings (negative delta) before openings
          events.sort((a, b) => a.time - b.time || a.delta - b.delta);
          let current = 0;
          let max = 0;
          for (const event of events) {
            current += event.delta;
            if (current > max) max = current;
          }
          return max > 0 ? max : null;
        })();

        setTradeData({
          tradeId: trade.trade_id || '---',
          tradeStatus: trade.status || '---',
          realizedProfitPercent: trade.realized_profit_percent !== null && trade.realized_profit_percent !== undefined ? trade.realized_profit_percent : '---',
          realizedProfitUsdt: trade.realized_profit_usdt !== null && trade.realized_profit_usdt !== undefined ? trade.realized_profit_usdt : '---',
          maxAllocatedCapital,
          positions: allPositions
        });

        // Set symbolId for orderbook fetching
        if (allPositions.length >= 1) {
          setSymbolId(allPositions[0].symbolId);
        }

        // Initialize volumes for all positions
        const initialVolumes = {};
        allPositions.forEach((pos, index) => {
          const posIsOpenOrClosed = pos.position.opened;
          const posOpeningVolume = pos.position.openingVolumeRaw;
          if (posIsOpenOrClosed && posOpeningVolume) {
            initialVolumes[index] = posOpeningVolume.toString();
          }
        });
        setVolumes(initialVolumes);

        // Load fundings
        try {
          const fundingsResponse = await fetch(`${API_BASE_URL}/api/trades/${tradeId}/fundings`);
          const fundingsData = await fundingsResponse.json();
          if (fundingsData.success) {
            setFundings(fundingsData.data || []);
            calculateRealizedFundings(fundingsData.data || []);
          }
        } catch (error) {
          console.error('Error loading fundings:', error);
          setFundings([]);
        }

        // Load position jobs for all positions
        const positionIds = allPositions.map(pos => pos.positionId);
        await loadPositionJobs(positionIds);

        // Load trade jobs
        await loadTradeJobs(trade.trade_id);

        // Load hedge strategies
        await loadHedgeStrategies(trade.trade_id);
      }
    } catch (error) {
      console.error('Error loading trade details:', error);
    }
  };

  const loadPositionJobs = async (positionIds) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trade-position-jobs?target_item_type=POSITION`);
      const data = await response.json();

      if (data.success) {
        // Filter jobs for all position IDs
        const filteredJobs = data.data.filter(job =>
          positionIds.includes(job.target_item_id)
        );
        setPositionJobs(filteredJobs);
      }
    } catch (error) {
      console.error('Error loading position jobs:', error);
      setPositionJobs([]);
    }
  };

  const loadTradeJobs = async (tradeIdValue) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trade-position-jobs?target_item_type=TRADE`);
      const data = await response.json();

      if (data.success) {
        // Filter jobs for this trade ID
        const filteredJobs = data.data.filter(job =>
          job.target_item_id === tradeIdValue
        );
        setTradeJobs(filteredJobs);
      }
    } catch (error) {
      console.error('Error loading trade jobs:', error);
      setTradeJobs([]);
    }
  };

  const loadHedgeStrategies = async (tradeIdValue) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/hedge-strategies?trade_id=${tradeIdValue}`);
      const data = await response.json();

      if (data.success) {
        setHedgeStrategies(data.data || []);

        // Load steps for all strategies to map positions to strategies
        const positionMap = new Map();
        for (const strategy of data.data || []) {
          const stepsResponse = await fetch(`${API_BASE_URL}/api/hedge-strategies/steps?hedge_strategy_id=${strategy.hedge_strategy_id}`);
          const stepsData = await stepsResponse.json();

          if (stepsData.success && stepsData.data) {
            stepsData.data.forEach(step => {
              if (step.position_id) {
                positionMap.set(step.position_id, {
                  hedgeStrategyId: strategy.hedge_strategy_id,
                  status: strategy.status || 'INACTIVE'
                });
              }
            });
          }
        }
        setPositionToStrategy(positionMap);
      }
    } catch (error) {
      console.error('Error loading hedge strategies:', error);
      setHedgeStrategies([]);
      setPositionToStrategy(new Map());
    }
  };

  const handleJobClick = (event, job) => {
    event.stopPropagation();
    const rect = event.target.getBoundingClientRect();

    // Estimate popup dimensions (based on the styled popup with maxWidth: 300px)
    const popupWidth = 300;
    const popupHeight = 280; // Estimate based on content

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate initial position (to the right of the element)
    let x = rect.right + 10;
    let y = rect.top;

    // Check if popup would go off the right edge
    if (x + popupWidth > viewportWidth) {
      // Position to the left of the element instead
      x = rect.left - popupWidth - 10;

      // If still off screen on the left, align to right edge of viewport
      if (x < 0) {
        x = viewportWidth - popupWidth - 10;
      }
    }

    // Check if popup would go off the bottom edge
    if (y + popupHeight > viewportHeight) {
      // Adjust upward
      y = viewportHeight - popupHeight - 10;

      // If still off screen on top, align to top
      if (y < 0) {
        y = 10;
      }
    }

    const position = { x, y };

    setJobDetailsPopup({
      show: true,
      job: job,
      position: position
    });
  };

  const closeJobDetailsPopup = () => {
    setJobDetailsPopup({
      show: false,
      job: null,
      position: { x: 0, y: 0 }
    });
  };

  // Close job details popup when any key is pressed
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (jobDetailsPopup.show) {
        closeJobDetailsPopup();
      }
    };

    if (jobDetailsPopup.show) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [jobDetailsPopup.show]);

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/trade-position-jobs/${jobId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        alert('Job deleted successfully!');
        closeJobDetailsPopup();
        // Refresh the jobs data
        if (tradeId) {
          await loadTradeJobs(tradeId);
        }
        // Refresh position jobs
        const positionIds = tradeData.positions.map(pos => pos.positionId).filter(id => id !== '---');
        if (positionIds.length > 0) {
          await loadPositionJobs(positionIds);
        }
      } else {
        alert(`Failed to delete job: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Error deleting job. Please try again.');
    }
  };

  const openTradeJobDialog = () => {
    if (!tradeData.positions || tradeData.positions.length < 2) {
      alert('Cannot create trade job: insufficient positions');
      return;
    }

    // Calculate estimated profit
    let fundingRateEx1 = tradeData.positions[0].fundingFee !== '---' && tradeData.positions[0].fundingFee !== 'N/A'
      ? parseFloat(tradeData.positions[0].fundingFee)
      : 0;
    let fundingRateEx2 = tradeData.positions[1].fundingFee !== '---' && tradeData.positions[1].fundingFee !== 'N/A'
      ? parseFloat(tradeData.positions[1].fundingFee)
      : 0;

    const contract1 = tradeData.positions[0].contract ? tradeData.positions[0].contract.toUpperCase() : '';
    const contract2 = tradeData.positions[1].contract ? tradeData.positions[1].contract.toUpperCase() : '';

    if (contract1 === 'LONG') {
      fundingRateEx1 = fundingRateEx1 * (-1);
    }
    if (contract2 === 'LONG') {
      fundingRateEx2 = fundingRateEx2 * (-1);
    }

    const takerFeeEx1 = tradeData.positions[0].takerFee !== '---' && tradeData.positions[0].takerFee !== 'N/A'
      ? parseFloat(tradeData.positions[0].takerFee)
      : 0;
    const takerFeeEx2 = tradeData.positions[1].takerFee !== '---' && tradeData.positions[1].takerFee !== 'N/A'
      ? parseFloat(tradeData.positions[1].takerFee)
      : 0;

    const estimatedProfit = fundingRateEx1 + fundingRateEx2 - (2 * takerFeeEx1) - (2 * takerFeeEx2);

    // Get volume from first position
    const ex1Volume = volumes[0] || '';

    // Calculate scheduled time based on contract types
    let scheduledTime = '';
    const isEx1NonSpot = contract1 === 'LONG' || contract1 === 'SHORT';
    const isEx2NonSpot = contract2 === 'LONG' || contract2 === 'SHORT';

    if (isEx1NonSpot || isEx2NonSpot) {
      // Get next funding time - use position 0 if non-spot, otherwise position 1
      const positionData = isEx1NonSpot ? tradeData.positions[0] : tradeData.positions[1];
      const nextFundingTime = getNextFundingTime(positionData.fundingRateDateTime, positionData.fundingHoursPeriod);

      // Subtract 5 minutes
      const scheduledDate = new Date(nextFundingTime);
      scheduledDate.setMinutes(scheduledDate.getMinutes() - 5);

      // Format for datetime-local input (YYYY-MM-DDTHH:MM)
      const year = scheduledDate.getFullYear();
      const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
      const day = String(scheduledDate.getDate()).padStart(2, '0');
      const hours = String(scheduledDate.getHours()).padStart(2, '0');
      const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');
      scheduledTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    setTradeJobDialogData({
      tradeId: tradeData.tradeId,
      actionType: 'OPEN-TRADE',
      triggerType: 'TRADE_PROFIT',
      condition: '>',
      tradeProfit: formatForDisplay(estimatedProfit),
      volume: ex1Volume,
      scheduledTime: scheduledTime
    });

    setShowTradeJobDialog(true);
  };

  const handleTradeJobDialogChange = (field, value) => {
    // When actionType is set to "CLOSE-AUTOMATIC", prefill scheduledTime with next funding time - 5 minutes
    if (field === 'actionType' && value === 'CLOSE-AUTOMATIC') {
      // Calculate next funding time - use position 0 if available, otherwise position 1
      if (!tradeData.positions || tradeData.positions.length === 0) {
        return;
      }
      const positionData = tradeData.positions[0].fundingRateDateTime ? tradeData.positions[0] : tradeData.positions[1];
      const nextFunding = getNextFundingTime(positionData.fundingRateDateTime, positionData.fundingHoursPeriod);

      // Subtract 5 minutes
      const scheduledDate = new Date(nextFunding);
      scheduledDate.setMinutes(scheduledDate.getMinutes() - 5);

      // Format for datetime-local input (YYYY-MM-DDTHH:MM)
      const year = scheduledDate.getFullYear();
      const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
      const day = String(scheduledDate.getDate()).padStart(2, '0');
      const hours = String(scheduledDate.getHours()).padStart(2, '0');
      const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');
      const scheduledTime = `${year}-${month}-${day}T${hours}:${minutes}`;

      setTradeJobDialogData(prev => ({
        ...prev,
        [field]: value,
        scheduledTime: scheduledTime
      }));
    } else {
      setTradeJobDialogData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleConfirmTradeJob = async () => {
    try {
      // Validate required fields
      if (!tradeJobDialogData.tradeId || !tradeJobDialogData.volume || !tradeJobDialogData.scheduledTime) {
        alert('Please fill in all required fields: Trade ID, Volume, and Scheduled Time');
        return;
      }

      // Prepare the job data
      const jobData = {
        target_item_id: tradeJobDialogData.tradeId,
        target_item_type: 'TRADE',
        action_type: tradeJobDialogData.actionType,
        trigger_type: tradeJobDialogData.triggerType,
        condition: tradeJobDialogData.condition,
        volume: parseFloat(tradeJobDialogData.volume),
        scheduled_time: tradeJobDialogData.scheduledTime,
        condition_value: tradeJobDialogData.tradeProfit !== '' ? parseFloat(tradeJobDialogData.tradeProfit) / 100 : null,
        status: 'SCHEDULED'
      };

      console.log('Creating trade job:', jobData);

      const response = await fetch(`${API_BASE_URL}/api/trade-position-jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobData)
      });

      const data = await response.json();

      if (data.success) {
        alert('Trade job created successfully!');
        setShowTradeJobDialog(false);
        // Reload trade jobs
        await loadTradeJobs(tradeJobDialogData.tradeId);
      } else {
        alert(`Failed to create trade job: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating trade job:', error);
      alert('Error creating trade job. Please try again.');
    }
  };

  // Generic volume change handler
  const handleVolumeChange = (positionIndex, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setVolumes(prev => ({ ...prev, [positionIndex]: value }));
    }
  };

  const calculateTotalPrice = (volume, unitPrice) => {
    if (!volume || volume === '' || unitPrice === '---') {
      return '---';
    }
    const volumeNum = parseFloat(volume);
    const priceNum = typeof unitPrice === 'number' ? unitPrice : parseFloat(unitPrice);
    if (isNaN(volumeNum) || isNaN(priceNum) || volumeNum === 0) {
      return '---';
    }
    return volumeNum * priceNum;
  };

  const handleOpen = async (type, positionIndexOrExchange) => {
    console.log(`Opening position with ${type} price for position ${positionIndexOrExchange}...`);

    try {
      // Support both old (exchange1/exchange2) and new (index) formats
      let positionIndex;
      if (positionIndexOrExchange === 'exchange1') {
        positionIndex = 0;
      } else if (positionIndexOrExchange === 'exchange2') {
        positionIndex = 1;
      } else {
        positionIndex = positionIndexOrExchange;
      }

      // Get position data
      const positionData = tradeData.positions[positionIndex];
      if (!positionData) {
        alert('Position data not available.');
        return;
      }

      const volume = volumes[positionIndex] || '';

      // Validate volume is entered
      if (!volume || volume === '' || parseFloat(volume) <= 0) {
        alert('Please enter a valid volume before opening the position.');
        return;
      }

      // Determine if taker or maker
      const isTaker = type === 'taker' ? 1 : 0;

      // Get contract type and taker fee
      const contractType = (positionData.contract || '').toUpperCase();
      // Get opening price based on type and contract
      // Save the price without fees. The fee will be added and calculated on backed when storing to DB
      let openingPrice;
      if (isTaker){
         if (contractType === 'LONG' || contractType === 'SPOT') {
            openingPrice = positionData.orderBook.bestAsk;
          } else {
            openingPrice = positionData.orderBook.bestAsk;
          }
      } else {
            openingPrice = positionData.orderBook.actualPrice;
      }

      // Validate opening price
      if (openingPrice === '---' || !openingPrice) {
        alert('Opening price is not available. Please refresh the orderbook data.');
        return;
      }

      // Prepare request body
      const requestBody = {
        position_id: positionData.positionId,
        exchange_id: positionData.exchangeId,
        symbol_id: symbolId,
        quantity: parseFloat(volume),
        taker: isTaker,
        opening_price: parseFloat(openingPrice)
      };

      console.log('Opening position with parameters:', requestBody);

      // Call API
      const response = await fetch(`${API_BASE_URL}/api/open-position-simulated`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        console.log('Position opened successfully:', result.data);
        // Calculate total fee in USDT (fee per unit * volume)
        const feePerUnit = result.data.details.realized_opening_fee;
        const volume = result.data.position.realized_opening_volume;
        const totalFeeUSDT = (feePerUnit * volume).toFixed(6);
        alert(`Position opened successfully!\nOpening Price: ${openingPrice} USDT\nFee for opening position: ${totalFeeUSDT} USDT`);

        // Reload trade details to update the UI
        loadTradeDetails(tradeId);
      } else {
        console.error('Failed to open position:', result.error);
        alert(`Failed to open position: ${result.error}`);
      }
    } catch (error) {
      console.error('Error opening position:', error);
      alert(`Error opening position: ${error.message}`);
    }
  };

  const handleJobButtonClick = (positionIndexOrExchange) => {
    // Support both old (exchange1/exchange2) and new (index) formats
    let positionIndex;
    if (positionIndexOrExchange === 'exchange1') {
      positionIndex = 0;
    } else if (positionIndexOrExchange === 'exchange2') {
      positionIndex = 1;
    } else {
      positionIndex = positionIndexOrExchange;
    }

    // Get position data
    const positionData = tradeData.positions[positionIndex];
    if (!positionData) {
      alert('Position data not available.');
      return;
    }

    const volume = volumes[positionIndex] || '';

    // Calculate default scheduled time (next funding time - 5 minutes)
    const nextFunding = getNextFundingTime(positionData.fundingRateDateTime, positionData.fundingHoursPeriod);
    const defaultTime = new Date(nextFunding.getTime() - 5 * 60 * 1000); // Subtract 5 minutes

    // Format time in local timezone for datetime-local input
    const year = defaultTime.getFullYear();
    const month = String(defaultTime.getMonth() + 1).padStart(2, '0');
    const day = String(defaultTime.getDate()).padStart(2, '0');
    const hours = String(defaultTime.getHours()).padStart(2, '0');
    const minutes = String(defaultTime.getMinutes()).padStart(2, '0');
    const formattedTime = `${year}-${month}-${day}T${hours}:${minutes}`;

    // Get funding rate (convert from percentage to decimal if available)
    let fundingRate = '';
    if (positionData.fundingFee !== '---' && positionData.fundingFee !== 'N/A') {
      fundingRate = positionData.fundingFee;
    }

    setJobDialogData({
      positionId: positionData.positionId,
      exchange: `position${positionIndex}`,
      actionType: 'OPEN',
      triggerType: 'funding_rate',
      condition: '>',
      volume: volume || '',
      scheduledTime: formattedTime,
      conditionValue: fundingRate
    });

    setShowJobDialog(true);
  };

  const handleJobDialogClose = () => {
    setShowJobDialog(false);
  };

  const handleJobDialogChange = (field, value) => {
    setJobDialogData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleJobDialogSubmit = async () => {
    try {
      // Validate required fields
      if (!jobDialogData.positionId || !jobDialogData.volume || !jobDialogData.scheduledTime) {
        alert('Please fill in all required fields.');
        return;
      }

      // Construct action_type and condition_value based on trigger type
      let action_type = jobDialogData.actionType;
      let condition_value = null;

      if (jobDialogData.triggerType === 'IF-PRICE') {
        // For IF-PRICE trigger: action_type becomes "OPEN-POSITION-IF-PRICE" or "CLOSE-POSITION-IF-PRICE"
        action_type = `${jobDialogData.actionType}-POSITION`;
        condition_value = jobDialogData.conditionValue;
      } else {
        // For funding_rate trigger: convert condition value to decimal
        condition_value = jobDialogData.conditionValue ? parseFloat(jobDialogData.conditionValue) / 100 : null;
      }

      // Prepare request body
      const requestBody = {
        target_item_id: jobDialogData.positionId,
        action_type: action_type,
        trigger_type: jobDialogData.triggerType,
        condition: jobDialogData.condition,
        volume: parseFloat(jobDialogData.volume),
        scheduled_time: new Date(jobDialogData.scheduledTime).toISOString(),
        status: 'SCHEDULED',
        condition_value: condition_value,
        target_item_type: 'POSITION'
      };

      console.log('Creating job with parameters:', requestBody);

      // Call API
      const response = await fetch(`${API_BASE_URL}/api/position-jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        console.log('Job created successfully:', result.data);
        alert('Job scheduled successfully!');
        setShowJobDialog(false);
        // Reload position jobs
        const positionIds = tradeData.positions.map(pos => pos.positionId).filter(id => id !== '---');
        await loadPositionJobs(positionIds);
      } else {
        console.error('Failed to create job:', result.error);
        alert(`Failed to create job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error creating job:', error);
      alert(`Error creating job: ${error.message}`);
    }
  };

  const handleClose = async (type, positionIndexOrExchange) => {
    console.log(`Closing position with ${type} price for position ${positionIndexOrExchange}...`);

    try {
      // Support both old (exchange1/exchange2) and new (index) formats
      let positionIndex;
      if (positionIndexOrExchange === 'exchange1') {
        positionIndex = 0;
      } else if (positionIndexOrExchange === 'exchange2') {
        positionIndex = 1;
      } else {
        positionIndex = positionIndexOrExchange;
      }

      // Get position data
      const positionData = tradeData.positions[positionIndex];
      if (!positionData) {
        alert('Position data not available.');
        return;
      }

      // Determine if taker or maker
      const isTaker = type === 'taker' ? 1 : 0;

      // Get closing price based on type
      // Save the price without fees. The fee will be added and calculated on backed when storing to DB
      const contractType = (positionData.contract || '').toUpperCase();
      let closingPrice;
      if (isTaker){
         if (contractType === 'LONG' || contractType === 'SPOT') {
            closingPrice = positionData.orderBook.bestSell;
          } else {
            closingPrice = positionData.orderBook.bestAsk;
          }
      } else {
            closingPrice = positionData.orderBook.actualPrice;
      }





      // Validate closing price
      if (closingPrice === '---' || !closingPrice) {
        alert('Closing price is not available. Please refresh the orderbook data.');
        return;
      }

      // Prepare request body
      const requestBody = {
        position_id: positionData.positionId,
        exchange_id: positionData.exchangeId,
        symbol_id: symbolId,
        taker: isTaker,
        closing_price: parseFloat(closingPrice)
      };

      console.log('Closing position with parameters:', requestBody);

      // Call API
      const response = await fetch(`${API_BASE_URL}/api/close-position-simulated`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        console.log('Position closed successfully:', result.data);

        // Calculate total fee in USDT (fee per unit * volume)
        const feePerUnit = result.data.details.realized_closing_fee;
        const volume = result.data.position.realized_opening_volume;
        const totalFeeUSDT = (feePerUnit * volume).toFixed(6);

        alert(`Position closed successfully!\nClosing Price: ${closingPrice} USDT\nFee for closing position: ${totalFeeUSDT} USDT`);

        // Reload trade details to update the UI
        loadTradeDetails(tradeId);
      } else {
        console.error('Failed to close position:', result.error);
        alert(`Failed to close position: ${result.error}`);
      }
    } catch (error) {
      console.error('Error closing position:', error);
      alert(`Error closing position: ${error.message}`);
    }
  };

  const renderActionButton = (positionStatus, type, positionIndexOrExchange) => {
    if (positionStatus === 'pending') {
      return (
        <button className="action-btn open-btn" onClick={() => handleOpen(type, positionIndexOrExchange)}>
          Open
        </button>
      );
    } else if (positionStatus === 'open') {
      return (
        <button className="action-btn close-btn" onClick={() => handleClose(type, positionIndexOrExchange)}>
          Close
        </button>
      );
    } else if (positionStatus === 'closed') {
      return (
        <span className="action-btn closed-btn">
          Closed
        </span>
      );
    }
  };

  const handlePositionAdded = (newPosition) => {
    console.log('New position added:', newPosition);
    // Reload trade details to show the new position
    loadTradeDetails(tradeId);
    // Window will automatically resize via the useEffect watching tradeData.positions.length
  };

  return (
    <div style={{ padding: '16px', paddingRight: '11px', height: '100%', overflow: 'auto' }}>
      {/* Trade Header */}
      <div className="trade-header">
        <table style={{ width: '100%', borderCollapse: 'collapse', margin: 0, padding: 0 }}>
          <tbody>
            <tr>
              {/* First cell: One-column / Two-lines table for labels and values */}
              <td style={{ margin: 0, padding: 0, verticalAlign: 'top', width: '1%' }}>
                <table style={{ width: 'auto', borderCollapse: 'collapse', margin: 0, padding: 0 }}>
                  <tbody>
                    <tr className="trade-info-row">
                      <td className="trade-label" style={{ width: '1%', whiteSpace: 'nowrap' }}>Trade id:</td>
                      <td className="trade-value" style={{ width: '1%', whiteSpace: 'nowrap' }}>{tradeData.tradeId}</td>
                    </tr>
                    <tr className="trade-info-row">
                      <td className="trade-label" style={{ width: '1%', whiteSpace: 'nowrap' }}>Trade status:</td>
                      <td className="trade-value" style={{ width: '1%', whiteSpace: 'nowrap' }}>{formatTradeStatusCompact(tradeData.tradeStatus)}</td>
                    </tr>
                    <tr className="trade-info-row">
                      <td className="trade-label" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        <span className="hide-mobile">Trade max allocated capital:</span>
                        <span className="show-mobile" style={{ display: 'none' }}>Max alc cap[USDT]:</span>
                      </td>
                      <td className="trade-value" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        {tradeData.maxAllocatedCapital !== null && tradeData.maxAllocatedCapital !== undefined
                          ? `${formatForDisplay(tradeData.maxAllocatedCapital, 2)}`
                          : '---'}
                      </td>
                    </tr>
                    <tr className="trade-info-row">
                      <td className="trade-label" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        <span className="hide-mobile">Trade realized profit [%]:</span>
                        <span className="show-mobile" style={{ display: 'none' }}>Rl profit [%]:</span>
                      </td>
                      <td className="trade-value" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        {tradeData.realizedProfitPercent !== '---'
                          ? `${formatForDisplay(tradeData.realizedProfitPercent)}`
                          : '---'}
                      </td>
                    </tr>
                    <tr className="trade-info-row">
                      <td className="trade-label" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        <span className="hide-mobile">Trade realized profit:</span>
                        <span className="show-mobile" style={{ display: 'none' }}>Rl profit [USDT]:</span>
                      </td>
                      <td className="trade-value" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        {tradeData.realizedProfitUsdt !== '---'
                          ? `${formatForDisplay(tradeData.realizedProfitUsdt)}`
                          : '--- USDT'}
                      </td>
                    </tr>
                    <tr className="trade-info-row">
                      <td className="trade-label" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        <span className="hide-mobile">Trade estimated profit w/o funding [%]:</span>
                        <span className="show-mobile" style={{ display: 'none' }}>Act opp prof[%]:</span>
                      </td>
                      <td className="trade-value" style={{color: tradeData.positions.length >= 2 && tradeData.positions[0].position?.status === 'closed' && tradeData.positions[1].position?.status === 'closed' ? '#8b949e' : 'inherit' }}>
                        {(() => {
                            if (!tradeData.positions || tradeData.positions.length < 2) {
                              return '---';
                            }
                            // Get funding rates
                            let fundingRateEx1 = tradeData.positions[0].fundingFee !== '---' && tradeData.positions[0].fundingFee !== 'N/A'
                              ? parseFloat(tradeData.positions[0].fundingFee)
                              : 0;
                            let fundingRateEx2 = tradeData.positions[1].fundingFee !== '---' && tradeData.positions[1].fundingFee !== 'N/A'
                              ? parseFloat(tradeData.positions[1].fundingFee)
                              : 0;

                            // Check if contract is LONG and invert funding rate
                            const contract1 = tradeData.positions[0].contract ? tradeData.positions[0].contract.toUpperCase() : '';
                            const contract2 = tradeData.positions[1].contract ? tradeData.positions[1].contract.toUpperCase() : '';

                            if (contract1 === 'LONG') {
                              fundingRateEx1 = fundingRateEx1 * (-1);
                            }
                            if (contract2 === 'LONG') {
                              fundingRateEx2 = fundingRateEx2 * (-1);
                            }

                            // Get taker fees
                            const takerFeeEx1 = tradeData.positions[0].takerFee !== '---' && tradeData.positions[0].takerFee !== 'N/A'
                              ? parseFloat(tradeData.positions[0].takerFee)
                              : 0;
                            const takerFeeEx2 = tradeData.positions[1].takerFee !== '---' && tradeData.positions[1].takerFee !== 'N/A'
                              ? parseFloat(tradeData.positions[1].takerFee)
                              : 0;

                            // Calculate: funding rate ex1 + funding rate ex2 - 2*taker fee ex1 - 2*taker fee ex2
                            const estimatedProfit = fundingRateEx1 + fundingRateEx2 - (2 * takerFeeEx1) - (2 * takerFeeEx2);

                            return tradeData.positions[0].fundingFee !== '---' && tradeData.positions[1].fundingFee !== '---'
                              ? `${formatForDisplay(estimatedProfit)}`
                              : '---';
                          })()}
                      </td>
                    </tr>
                    <tr className="trade-info-row">
                      <td className="trade-label" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                        Hedge strategies:
                      </td>
                      <td className="trade-value">
                      </td>
                    </tr>
                    {hedgeStrategies.length > 0 && (
                      <tr className="trade-info-row">
                        <td colSpan="2" className="trade-value" style={{ paddingLeft: '0', textAlign: 'left' }}>
                          <div style={{ fontSize: '11px' }}>
                            {hedgeStrategies.map(strategy => (
                              <div
                                key={strategy.hedge_strategy_id}
                                onClick={() => {
                                  if (onOpenHedgeCalculator && tradeData.positions.length > 0) {
                                    onOpenHedgeCalculator(tradeData.positions[0], strategy.hedge_strategy_id);
                                  }
                                }}
                                style={{
                                  color: '#8b949e',
                                  cursor: 'pointer',
                                  padding: '2px 0',
                                  transition: 'color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#58a6ff'}
                                onMouseLeave={(e) => e.target.style.color = '#8b949e'}
                              >
                                ID: {strategy.hedge_strategy_id} | Target: {strategy.net_target_profit} USDT | Created: {formatTimestamp(strategy.timestamp_created)}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              {/* Second cell/row: One-column / Two-lines table for labels and values */}
              <td style={{ margin: 0, paddingTop: '10px', verticalAlign: 'top' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', margin: 0, padding: 0 }}>
                  <tbody>
                      {/* Trade Job and Add Position buttons */}
                      <tr className="trade-info-row">
                        <td className="trade-value" style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={openTradeJobDialog}
                            style={{
                              padding: '2px 6px',
                              fontSize: '11px',
                              fontFamily: "'Courier New', Courier, monospace",
                              backgroundColor: '#000000',
                              color: '#1e90ff',
                              border: '1px solid #1e90ff',
                              borderRadius: '2px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              lineHeight: '1',
                              textAlign: 'left',
                              whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#0a0a0a';
                              e.target.style.color = '#4169e1';
                              e.target.style.borderColor = '#4169e1';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#000000';
                              e.target.style.color = '#1e90ff';
                              e.target.style.borderColor = '#1e90ff';
                            }}
                          >
                            New Trade Job
                          </button>
                          <button
                            onClick={() => setShowAddPositionDialog(true)}
                            style={{
                              padding: '2px 6px',
                              fontSize: '11px',
                              fontFamily: "'Courier New', Courier, monospace",
                              backgroundColor: '#000000',
                              color: '#238636',
                              border: '1px solid #238636',
                              borderRadius: '2px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              lineHeight: '1',
                              textAlign: 'left',
                              whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#0a0a0a';
                              e.target.style.color = '#2ea043';
                              e.target.style.borderColor = '#2ea043';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#000000';
                              e.target.style.color = '#238636';
                              e.target.style.borderColor = '#238636';
                            }}
                          >
                            Add new Position
                          </button>
                        </td>
                      </tr>
                      {/* Trade Jobs */}
                      <tr className="trade-info-row">
                        <td className="trade-label" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                          Trade jobs:
                        </td>
                        <td className="trade-value">
                        </td>
                      </tr>
                      {tradeJobs.length > 0 && (
                        <tr className="trade-info-row">
                          <td colSpan="2" className="trade-value" style={{ paddingLeft: '0', textAlign: 'left' }}>
                            <div style={{ fontSize: '11px' }}>
                              {tradeJobs.map((job, index) => (
                                <div
                                  key={`trade-job-${index}`}
                                  onClick={(e) => handleJobClick(e, job)}
                                  style={{
                                    color: '#8b949e',
                                    cursor: 'pointer',
                                    padding: '2px 0',
                                    transition: 'color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.color = '#58a6ff'}
                                  onMouseLeave={(e) => e.target.style.color = '#8b949e'}
                                >
                                  ID: {job.job_id} | {job.status} {job.action_type}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </td>

              {/* Second cell: Refresh button */}
              <td style={{ margin: 0, padding: 0, paddingLeft: '8px', verticalAlign: 'top' }}>
                <button
                  onClick={() => loadTradeDetails(tradeId)}
                  style={{
                    backgroundColor: '#0d1117',
                    color: '#8b949e',
                    border: '1px solid #30363d',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#161b22';
                    e.target.style.borderColor = '#58a6ff';
                    e.target.style.color = '#58a6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#0d1117';
                    e.target.style.borderColor = '#30363d';
                    e.target.style.color = '#8b949e';
                  }}
                  title="Refresh trade details"
                >
                  ⟳ REFRESH
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      { /*Exchange Details Section */}
      {/*
      <div className="section-divider">
        <div className="exchange-row">
          <span className="exchange-cell label" style={{textAlign: 'left'}} >Exchange 1</span>
          <span className="exchange-cell value">&nbsp;</span>
          <span className="exchange-cell label" style={{textAlign: 'left'}} >Exchange 2</span>
          <span className="exchange-cell value">&nbsp;</span>
        </div>
      </div>
      */}

      <table className="exchange-details-grid" ref={exchangeGridRef}>
      <tbody>

        {/* Header Row */}
        <tr className="exchange-row" style={{height: '8px', borderTop: '1px solid #FFFFFF'}}>
          <td className="exchange-cell label"></td>
          {tradeData.positions.map((pos, index) => (
            <td key={`header-${index}`} className="exchange-cell value" style={{fontWeight: 'bold', color: 'lightgray'}}>
              Exchange {index + 1}
            </td>
          ))}
        </tr>

        {/* Row: Exchange */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Exchange:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`exchange-${index}`} className="exchange-cell value">{pos.name}</td>
          ))}
        </tr>

        {/* Row: Symbol */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Symbol:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`symbol-${index}`} className="exchange-cell value">
              <a
                href={buildTradingViewUrl(pos.name, pos.symbol, pos.contract)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#c9d1d9', textDecoration: 'underline', cursor: 'pointer' }}
              >
                {pos.symbol}
              </a>
            </td>
          ))}
        </tr>

        {/* Row: Contract */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Contract:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`contract-${index}`} className="exchange-cell value">{pos.contract}</td>
          ))}
        </tr>

        {/* Row: Position ID */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Position ID:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`position-id-${index}`} className="exchange-cell value">{pos.positionId}</td>
          ))}
        </tr>

        {/* Row: Status */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Status:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`status-${index}`} className="exchange-cell value">{pos.position.status}</td>
          ))}
        </tr>


        {/* Row: Funding Fee */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Funding fee:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`funding-fee-${index}`} className={`exchange-cell value ${getValueClass(pos.fundingFee).includes('na-value') ? 'na-value' : ''}`}>
              {pos.fundingFee}
            </td>
          ))}
        </tr>


        {/* Row: Next Funding Time */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Next funding:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`next-funding-${index}`} className="exchange-cell value" style={{ lineHeight: '1.0', padding: '0px 8px 0px 0px' }}>
              <div style={{ color: '#8b949e', fontSize: '11px' }}>
                {(() => {
                  const nextFunding = getNextFundingTime(pos.fundingRateDateTime, pos.fundingHoursPeriod);
                  if (!nextFunding) return '-';
                  return formatNextFundingTime(nextFunding).split(' ')[0];
                })()}
              </div>
              <div style={{ color: '#8b949e', fontSize: '11px' }}>
                {(() => {
                  const nextFunding = getNextFundingTime(pos.fundingRateDateTime, pos.fundingHoursPeriod);
                  if (!nextFunding) return '-';
                  return formatNextFundingTime(nextFunding).split(' ')[1];
                })()}
              </div>
              <div style={{ color: formatCountdown(getNextFundingTime(pos.fundingRateDateTime, pos.fundingHoursPeriod)).color, fontSize: '11px' }}>
                {formatCountdown(getNextFundingTime(pos.fundingRateDateTime, pos.fundingHoursPeriod)).text}
              </div>
            </td>
          ))}
        </tr>


        {/* Row: Taker fee */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Taker fee:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`taker-fee-${index}`} className={`exchange-cell value ${getValueClass(pos.takerFee).includes('na-value') ? 'na-value' : ''}`}>
              {pos.takerFee === '---' ? '<unknown>' : pos.takerFee}
            </td>
          ))}
        </tr>

        {/* Row: Maker fee */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Maker fee:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`maker-fee-${index}`} className={`exchange-cell value ${getValueClass(pos.makerFee).includes('na-value') ? 'na-value' : ''}`}>
              {pos.makerFee === '---' ? '<unknown>' : pos.makerFee}
            </td>
          ))}
        </tr>
        {/* Empty row */}
        <tr className="exchange-row" style={{height: '4px'}}>
          <td className="exchange-cell"></td>
          {tradeData.positions.map((pos, index) => (
            <td key={`empty-1-${index}`} className="exchange-cell"></td>
          ))}
        </tr>

        {/* Row: Best bid */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Best bid:</td>
          {tradeData.positions.map((pos, index) => {
            const hasError = orderbookErrors[index];
            const errorMsg = orderbookErrors[index];
            return (
              <td key={`best-bid-${index}`} className="exchange-cell value best-bid-value">
                {hasError ? (
                  <span style={{ color: '#f85149', cursor: 'help' }} title={errorMsg}>err</span>
                ) : (
                  formatForDisplay(pos.orderBook.bestSell, 6)
                )}
              </td>
            );
          })}
        </tr>

        {/* Row: Actual price */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Actual price:</td>
          {tradeData.positions.map((pos, index) => {
            const hasError = orderbookErrors[index];
            const errorMsg = orderbookErrors[index];
            return (
              <td key={`actual-price-${index}`} className="exchange-cell value">
                {hasError ? (
                  <span style={{ color: '#f85149', cursor: 'help' }} title={errorMsg}>err</span>
                ) : (
                  formatForDisplay(pos.orderBook.actualPrice, 6)
                )}
              </td>
            );
          })}
        </tr>

        {/* Diff row */}
        <tr className="exchange-row diff-sum-row">
          <td className="exchange-cell label"></td>
          <td className="exchange-cell value" colSpan={tradeData.positions.length} style={{ textAlign: 'center', color: '#8b949e' }}>
            {(() => {
              if (tradeData.positions.length < 2) return '---';
              const val1 = tradeData.positions[0].orderBook.actualPrice;
              const val2 = tradeData.positions[1].orderBook.actualPrice;
              if (orderbookErrors[0] || orderbookErrors[1] || val1 === '---' || val2 === '---' || !val1 || !val2) {
                return '---';
              }
              const diff = parseFloat(val1) - parseFloat(val2);
              return <><span style={{ verticalAlign: 'middle' }}>↑</span> {formatForDisplay(diff)}</>;
            })()}
          </td>
        </tr>
        {/* Row: Best ask */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Best ask:</td>
          {tradeData.positions.map((pos, index) => {
            const hasError = orderbookErrors[index];
            const errorMsg = orderbookErrors[index];
            return (
              <td key={`best-ask-${index}`} className="exchange-cell value best-ask-value">
                {hasError ? (
                  <span style={{ color: '#f85149', cursor: 'help' }} title={errorMsg}>err</span>
                ) : (
                  formatForDisplay(pos.orderBook.bestAsk, 6)
                )}
              </td>
            );
          })}
        </tr>
        {/* Empty row */}
        <tr className="exchange-row" style={{height: '4px'}}>
          <td className="exchange-cell"></td>
          {tradeData.positions.map((pos, index) => (
            <td key={`empty-2-${index}`} className="exchange-cell"></td>
          ))}
        </tr>

        {/* Row: Depth To Move (DTM 0.1%) */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            <span className="hide-mobile">Depth To Move (DTM 0.1%)[USDT]:</span>
            <span className="show-mobile" style={{ display: 'none' }}>DTM 0.1%[USDT]:</span>
          </td>
          {tradeData.positions.map((pos, index) => {
            const hasError = dtmErrors[index];
            const errorMsg = dtmErrors[index];
            return (
              <td key={`dtm-${index}`} className="exchange-cell value">
                {hasError ? (
                  <span style={{ color: '#f85149', cursor: 'help' }} title={errorMsg}>err</span>
                ) : (
                  formatForDisplay(pos.orderBook.depthToMove, getOptimalDecimalPlaces(7, pos.orderBook.depthToMove, 4))
                )}
              </td>
            );
          })}
        </tr>

        {/* Row: Depth To Move (DTM 0.1%) volume */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            <span className="hide-mobile">Depth To Move (DTM 0.1%)[volume]:</span>
            <span className="show-mobile" style={{ display: 'none' }}>DTM[vol]:</span>
          </td>
          {tradeData.positions.map((pos, index) => {
            const hasError = dtmErrors[index];
            const errorMsg = dtmErrors[index];
            return (
              <td key={`dtm-volume-${index}`} className="exchange-cell value">
                {hasError ? (
                  <span style={{ color: '#f85149', cursor: 'help' }} title={errorMsg}>err</span>
                ) : (
                  formatForDisplay(pos.orderBook.depthToMoveVolume, getOptimalDecimalPlaces(7, pos.orderBook.depthToMoveVolume, 4))
                )}
              </td>
            );
          })}
        </tr>



        {/* Empty row */}
        <tr className="exchange-row" style={{height: '4px'}}>
          <td className="exchange-cell"></td>
          {tradeData.positions.map((pos, index) => (
            <td key={`empty-3-${index}`} className="exchange-cell"></td>
          ))}
        </tr>

        {/* Row: Taker price */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            <span className="hide-mobile">
              Taker price for 1 {tradeData.positions[0]?.symbol || '---'}:
              <InfoIcon tooltipText={getTooltipText('takerPrice')} position="right" />
            </span>
            <span className="show-mobile" style={{ display: 'none' }}>
              Taker price:
              <InfoIcon tooltipText={getTooltipText('takerPrice')} position="right" />
            </span>
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`taker-price-${index}`} className="exchange-cell value">
              {formatForDisplay(pos.orderBook.takerPrice)}
            </td>
          ))}
        </tr>

        {/* Row: Maker price */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            <span className="hide-mobile">
              Maker price for 1 {tradeData.positions[0]?.symbol || '---'}:
              <InfoIcon tooltipText={getTooltipText('makerPrice')} position="right" />
            </span>
            <span className="show-mobile" style={{ display: 'none' }}>
              Maker price:
              <InfoIcon tooltipText={getTooltipText('makerPrice')} position="right" />
            </span>
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`maker-price-${index}`} className="exchange-cell value">
              {formatForDisplay(pos.orderBook.makerPrice)}
            </td>
          ))}
        </tr>


        {/* Row: Volume Input */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Volume:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`volume-${index}`} className="exchange-cell value" style={{paddingRight: '5px'}}>
              <input
                type="text"
                value={volumes[index] || ''}
                onChange={(e) => handleVolumeChange(index, e.target.value)}
                placeholder="0.00"
                disabled={pos.position.status === 'open' || pos.position.status === 'closed'}
                style={{
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '4px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  width: '80px',
                  textAlign: 'right',
                  opacity: (pos.position.status === 'open' || pos.position.status === 'closed') ? 0.6 : 1,
                  cursor: (pos.position.status === 'open' || pos.position.status === 'closed') ? 'not-allowed' : 'text'
                }}
              />
            </td>
          ))}
        </tr>


        {/* Row: Open/Close as taker */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            {tradeData.positions[0]?.position.status === 'open' ? 'Close as taker:' : 'Open as taker:'}
            <InfoIcon tooltipText={getTooltipText('openAsTaker')} position="right" />
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`taker-price-${index}`} className={`exchange-cell value ${pos.contract.toUpperCase() === 'SHORT' ? 'best-ask-value' : 'best-bid-value' }`} style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingRight: '5px' }}>
              {formatForDisplay(calculateTotalPrice(volumes[index], pos.orderBook.takerPrice))}
            </td>
          ))}
        </tr>
        {/* button only line  */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
             {/* empty cell */}
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`taker-btn-${index}`} className="exchange-cell value" style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingRight: '5px'}}>
              {renderActionButton(pos.position.status, 'taker', index)}
            </td>
          ))}
        </tr>

        {/* Row: Open/Close as maker */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            {tradeData.positions[0]?.position.status === 'open' ? 'Close as maker:' : 'Open as maker:'}
            <InfoIcon tooltipText={getTooltipText('openAsMaker')} position="right" />
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`maker-price-${index}`} className={`exchange-cell value ${pos.contract.toUpperCase() === 'SHORT' ? 'best-ask-value' : 'best-bid-value' }`} style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingRight: '5px' }}>
              {formatForDisplay(calculateTotalPrice(volumes[index], pos.orderBook.makerPrice))}
            </td>
          ))}
        </tr>
        {/* button only line  */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
             {/* empty cell */}
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`maker-btn-${index}`} className="exchange-cell value" style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingRight: '5px'}}>
              {renderActionButton(pos.position.status, 'maker', index)}
            </td>
          ))}
        </tr>

        {/* Empty row */}
        <tr className="exchange-row" style={{height: '8px', borderBottom: '1px solid #FFFFFF'}}>
          <td className="exchange-cell"></td>
          {tradeData.positions.map((pos, index) => (
            <td key={`empty-action-${index}`} className="exchange-cell"></td>
          ))}
        </tr>

        {/* Row: Position header */}
        <tr className="exchange-row" style={{ paddingTop: '10px'}}>
          <td className="exchange-cell label" style={{paddingTop: '3px', color: '#ffffff'}}>Position OPENED:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`opening-timestamp-${index}`} className="exchange-cell value" style={{ lineHeight: '1.0', padding: '0px 8px 0px 0px' }}>
              <div style={{ color: '#8b949e', fontSize: '11px' ,paddingTop: '3px'}}>
                {pos.position.openingTimestamp !== '---'
                  ? pos.position.openingTimestamp.split(',')[0]
                  : '---'}
              </div>
              <div style={{ color: '#8b949e', fontSize: '11px' }}>
                {pos.position.openingTimestamp !== '---'
                  ? pos.position.openingTimestamp.split(',')[1]?.trim()
                  : '---'}
              </div>
            </td>
          ))}
        </tr>

        {/* Row: Opening funding rate */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Opn. funding rate:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`opening-funding-rate-${index}`} className="exchange-cell value" style={{fontSize: '11px',color: '#ffffff'}}>
              {pos.position.realized_opening_funding_rate !== null && pos.position.realized_opening_funding_rate !== undefined && pos.position.realized_opening_funding_rate !== '---'
                ? `${(parseFloat(pos.position.realized_opening_funding_rate) * 100).toFixed(4)}%`
                : '<empty>'}
            </td>
          ))}
        </tr>

        {/* Row: Position opened at */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Position opened at:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`opened-at-${index}`} className="exchange-cell value">
              {pos.position.openedAt}
            </td>
          ))}
        </tr>

        {/* Diff row */}
        {/* <tr className="exchange-row diff-sum-row" style={{ borderBottom: '1px solid #30363d'}}>
          <td className="exchange-cell label"></td>
          <td className="exchange-cell value" colSpan="2" style={{ textAlign: 'center', color: '#8b949e' }}>
            {(() => {
              const val1 = tradeData.positions[0]?.position.openedAt;
              const val2 = tradeData.positions[1]?.position.openedAt;
              if (val1 === '---' || val2 === '---' || !val1 || !val2) {
                return '---';
              }
              const diff = parseFloat(val1) - parseFloat(val2);
              return <><span style={{ verticalAlign: 'middle' }}>↑</span> {formatForDisplay(diff)}</>;
            })()}
          </td>
        </tr>  */}

        {/* Row: Opening paid fee */}
        <tr className="exchange-row" >
          <td className="exchange-cell label">Opening paid fee:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`opening-paid-fee-${index}`} className="exchange-cell value" style={{ lineHeight: '1.0', padding: '0px 8px 0px 0px' }}>
              {pos.position.openingFeePercent !== '---' ? (
                <>
                  <div style={{display: 'flex',justifyContent: 'space-between',alignItems: 'center',fontSize: '11px',color: '#ffffff', whiteSpace: 'nowrap' }}>
                    <span style={{ textAlign: 'left', color: '#8b949e' }}>%</span><span style={{ textAlign: 'right' }}>{pos.position.openingFeePercent}</span>
                  </div>
                  <div style={{display: 'flex',justifyContent: 'space-between',alignItems: 'center',fontSize: '11px',color: '#ffffff', whiteSpace: 'nowrap' }}>
                    <span style={{ textAlign: 'left', color: '#8b949e' }}>USDT</span><span style={{ textAlign: 'right' }}>{pos.position.openingPaidFeeUsdt}</span>
                  </div>
                </>
              ) : '---'}
            </td>
          ))}
        </tr>

        {/* Row: Paid for position */}
        <tr className="exchange-row" style={{ borderBottom: '1px solid #FFFFFF'}}>
          <td className="exchange-cell label">
            Paid/gain of position:
            <span
              style={{ marginLeft: '5px', cursor: 'help', color: '#8b949e' }}
              title={`LONG = price*volume + fee
SHORT = price*volume - fee`}
            >
              ⓘ
            </span>
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`paid-for-position-${index}`} className="exchange-cell value">
              {pos.position.paidOrGainForPosition !== '---'
                ? formatForDisplay(pos.position.paidOrGainForPosition, getOptimalDecimalPlaces(7, pos.position.paidOrGainForPosition, 4))
                : '---'}
            </td>
          ))}
        </tr>

        {/* Diff row */}
        {/* <tr className="exchange-row diff-sum-row" style={{ borderBottom: '1px solid #30363d'}}>
          <td className="exchange-cell label"></td>
          <td className="exchange-cell value" colSpan="2" style={{ textAlign: 'center', color: '#8b949e' }}>
            {(() => {
              const val1 = tradeData.positions[0]?.position.paidOrGainForPosition;
              const val2 = tradeData.positions[1]?.position.paidOrGainForPosition;
              if (val1 === '---' || val2 === '---' || !val1 || !val2) {
                return '---';
              }
              const diff = parseFloat(val1) - parseFloat(val2);
              return <><span style={{ verticalAlign: 'top' }}>↑</span> &nbsp; {formatForDisplay(diff, 2)} &nbsp; <span style={{ verticalAlign: 'top' }}>↑</span></>;
            })()}
          </td>
        </tr> */}

        {/* Row: Position CLOSED */}
        <tr className="exchange-row" >
          <td className="exchange-cell label" style={{color: '#ffffff'}}>Position CLOSED:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`closing-timestamp-${index}`} className="exchange-cell value" style={{ lineHeight: '1.0', padding: '0px 8px 0px 0px' }}>
              <div style={{ color: '#8b949e', fontSize: '11px',paddingTop: '3px' }}>
                {pos.position.closingTimestamp !== '---'
                  ? pos.position.closingTimestamp.split(',')[0]
                  : '---'}
              </div>
              <div style={{ color: '#8b949e', fontSize: '11px' }}>
                {pos.position.closingTimestamp !== '---'
                  ? pos.position.closingTimestamp.split(',')[1]?.trim()
                  : '---'}
              </div>
            </td>
          ))}
        </tr>

        {/* Row: Closing funding rate */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Cls. funding rate:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`closing-funding-rate-${index}`} className="exchange-cell value" style={{fontSize: '11px',color: '#ffffff'}}>
              {pos.position.realized_closing_funding_rate !== null && pos.position.realized_closing_funding_rate !== undefined && pos.position.realized_closing_funding_rate !== '---'
                ? `${(parseFloat(pos.position.realized_closing_funding_rate) * 100).toFixed(4)}%`
                : '<empty>'}
            </td>
          ))}
        </tr>

        {/* Row: Position closed at */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Position closed at:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`closed-at-${index}`} className="exchange-cell value">
              {pos.position.closedAt}
            </td>
          ))}
        </tr>

        {/* Diff row */}
        {/* <tr className="exchange-row diff-sum-row" style={{ borderBottom: '1px solid #30363d'}}>
          <td className="exchange-cell label"></td>
          <td className="exchange-cell value" colSpan="2" style={{ textAlign: 'center', color: '#8b949e' }}>
            {(() => {
              const val1 = tradeData.positions[0]?.position.closedAt;
              const val2 = tradeData.positions[1]?.position.closedAt;
              if (val1 === '---' || val2 === '---' || !val1 || !val2) {
                return '---';
              }
              const diff = parseFloat(val1) - parseFloat(val2);
              return <><span style={{ verticalAlign: 'middle' }}>↑</span> &nbsp;  {formatForDisplay(diff)} &nbsp; <span style={{ verticalAlign: 'middle' }}>↑</span></>;
            })()}
          </td>
        </tr> */}

        {/* Row: Closing paid fee */}
        <tr className="exchange-row" >
          <td className="exchange-cell label">Closing paid fee: </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`closing-paid-fee-${index}`} className="exchange-cell value" style={{  lineHeight: '1.0', padding: '0px 8px 0px 0px' }}>
              {pos.position.closingFeePercent !== '---' ? (
                <>
                  <div style={{ display: 'flex',justifyContent: 'space-between',alignItems: 'center',fontSize: '11px',color: '#ffffff', whiteSpace: 'nowrap' }}>
                    <span style={{ textAlign: 'left', color: '#8b949e' }}>%</span><span style={{ textAlign: 'right' }}>{pos.position.closingFeePercent}</span>
                  </div>
                  <div style={{ display: 'flex',justifyContent: 'space-between',alignItems: 'center',fontSize: '11px',color: '#ffffff' }}>
                    <span style={{ textAlign: 'left', color: '#8b949e' }}>USDT</span><span style={{ textAlign: 'right' }}>{pos.position.closingPaidFeeUsdt}</span>
                  </div>
                </>
              ) : '---'}
            </td>
          ))}
        </tr>

        {/* Row: Closed position for */}
        <tr className="exchange-row" style={{ borderBottom: '1px solid #FFFFFF'}}>
          <td className="exchange-cell label">
            Gain/Paid of pos.:
            <span
              style={{ marginLeft: '5px', cursor: 'help', color: '#8b949e' }}
              title={`LONG = price*volume - fee
SHORT = price*volume + fee`}
            >
              ⓘ
            </span>
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`closed-position-for-${index}`} className="exchange-cell value">
              {pos.position.closedPositionFor !== '---'
                ? formatForDisplay(pos.position.closedPositionFor, getOptimalDecimalPlaces(7, pos.position.closedPositionFor, 4))
                : '---'}
            </td>
          ))}
        </tr>
        <tr className="exchange-row" >
          <td className="exchange-cell label" style={{color: '#ffffff'}}>Position PNL:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`pnl-header-${index}`} className="exchange-cell value" style={{ lineHeight: '1.0', padding: '0px 8px 0px 8px' }}>
            </td>
          ))}
        </tr>

        {/* Row: Hedge Assignment */}
        <tr className="exchange-row">
          <td className="exchange-cell label">Hedge assignment:</td>
          {tradeData.positions.map((pos, index) => {
            const hedgeOrder = pos.position?.hedgePositionOrder || 0;
            const isDisabled = hedgeOrder >= 2;
            const strategyData = positionToStrategy.get(pos.positionId);
            const isAssigned = strategyData !== undefined;
            const strategyId = strategyData?.hedgeStrategyId;
            const isActive = strategyData?.status === 'ACTIVE';

            return (
              <td key={`hedge-calc-button-${index}`} className="exchange-cell value">
                <button
                  onClick={() => {
                    if (!isDisabled && onOpenHedgeCalculator) {
                      // If assigned, pass the strategy ID; otherwise pass 'new' to indicate new strategy
                      onOpenHedgeCalculator(pos, isAssigned ? strategyId : 'new');
                    }
                  }}
                  disabled={isDisabled}
                  style={{
                    backgroundColor: '#0d1117',
                    color: isDisabled ? '#484f58' : (isAssigned && isActive ? '#3fb950' : '#8b949e'),
                    border: isDisabled ? '1px solid #484f58' : (isAssigned && isActive ? '1px solid #3fb950' : '1px solid #8b949e'),
                    borderRadius: '2px',
                    padding: '2px 8px',
                    margin: '0',
                    fontSize: '11px',
                    fontFamily: "'Courier New', Courier, monospace",
                    fontWeight: 'normal',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    minWidth: '60px',
                    opacity: isDisabled ? 0.5 : 1,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled) {
                      e.target.style.backgroundColor = '#161b22';
                      if (isAssigned && isActive) {
                        e.target.style.borderColor = '#56d364';
                      } else {
                        e.target.style.borderColor = '#c9d1d9';
                      }
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDisabled) {
                      e.target.style.backgroundColor = '#0d1117';
                      if (isAssigned && isActive) {
                        e.target.style.borderColor = '#3fb950';
                      } else {
                        e.target.style.borderColor = '#8b949e';
                      }
                    }
                  }}
                >
                  {isAssigned ? `Hedge ${strategyId} ${isActive ? 'active' : 'inactive'}` : 'Hedge Calc'}
                </button>
              </td>
            );
          })}
        </tr>

        {/* Row: Actual position PNL */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            <span className="hide-mobile">
              Actual position PNL[USDT]:
              <InfoIcon tooltipText={getTooltipText('actualPositionPnl')} position="right" />
            </span>
            <span className="show-mobile" style={{ display: 'none' }}>
              Act. PNL:
              <InfoIcon tooltipText={getTooltipText('actualPositionPnl')} position="right" />
            </span>
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`actual-pnl-usdt-${index}`} className="exchange-cell value" style={{
              color: pos.position.actualPnlUsdt !== '---' && parseFloat(pos.position.actualPnlUsdt) >= 0 ? '#3fb950' :
                     pos.position.actualPnlUsdt !== '---' && parseFloat(pos.position.actualPnlUsdt) < 0 ? '#f85149' : '#ffffff'
            }}>
              {pos.position.actualPnlUsdt !== '---' ? formatForDisplay(pos.position.actualPnlUsdt, getOptimalDecimalPlaces(7, pos.position.actualPnlUsdt, 4)) : '---'}
            </td>
          ))}
        </tr>

        {/* Sum row */}
        <tr className="exchange-row diff-sum-row" >
          <td className="exchange-cell label"></td>
          <td className="exchange-cell value" colSpan={tradeData.positions.length} style={{textAlign: 'center', color: '#8b949e', fontSize: '11px' }}>
            {(() => {
              const sum = tradeData.positions.reduce((acc, pos) => {
                if (pos.position.actualPnlUsdt !== '---') {
                  return acc + parseFloat(pos.position.actualPnlUsdt);
                }
                return acc;
              }, 0);

              // Calculate optimal hedge price
              const optimalHedgePrice = calculateOptimalHedgePrice(tradeData.positions);
              const optimalHedgePriceFormatted = optimalHedgePrice !== null
                ? formatForDisplay(optimalHedgePrice, getOptimalDecimalPlacesForHedge(7, optimalHedgePrice, 4))
                : null;

              const hasValidValues = tradeData.positions.some(pos => pos.position.actualPnlUsdt !== '---');

              return hasValidValues
                ? `Sum: ${formatForDisplay(sum, 2)} ${optimalHedgePriceFormatted !== null ? ` [${optimalHedgePriceFormatted}]` : ''}`
                : '---';
            })()}
          </td>
        </tr>

        {/* Row: Actual position PNL [%] */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            <span className="hide-mobile">
              Actual position PNL[%]:
              <InfoIcon tooltipText={getTooltipText('actualPositionPnl')} position="right" />
            </span>
            <span className="show-mobile" style={{ display: 'none' }}>
              Act. PNL:
              <InfoIcon tooltipText={getTooltipText('actualPositionPnl')} position="right" />
            </span>
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`actual-pnl-percent-${index}`} className="exchange-cell value" style={{
              color: pos.position.actualPnlUsdt !== '---' && parseFloat(pos.position.actualPnlUsdt) >= 0 ? '#3fb950' :
                     pos.position.actualPnlUsdt !== '---' && parseFloat(pos.position.actualPnlUsdt) < 0 ? '#f85149' : '#ffffff'
            }}>
              {pos.position.actualPnlUsdt !== '---' ? formatForDisplay(pos.position.actualPnl) : '---'}
            </td>
          ))}
        </tr>


        {/* Sum row */}
        <tr className="exchange-row diff-sum-row" >
          <td className="exchange-cell label"></td>
          <td className="exchange-cell value" colSpan={tradeData.positions.length} style={{textAlign: 'center', color: '#8b949e', fontSize: '11px' }}>
            {(() => {
              const sum = tradeData.positions.reduce((acc, pos) => {
                if (pos.position.actualPnl !== '---') {
                  return acc + parseFloat(pos.position.actualPnl);
                }
                return acc;
              }, 0);
              const hasValidValues = tradeData.positions.some(pos => pos.position.actualPnl !== '---');
              return hasValidValues ? `Sum: ${formatForDisplay(sum)}` : '---';
            })()}
          </td>
        </tr>

        {/* Row: Actual realized fundings */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            <span className="hide-mobile">Actual realized fundings:</span>
            <span className="show-mobile" style={{ display: 'none' }}>Act. fundings:</span>
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`actual-realized-fundings-${index}`} className={`exchange-cell value ${getValueClass(pos.position.actualRealizedFundings).includes('na-value') ? 'na-value' : ''}`} style={{
              color: pos.position.actualRealizedFundings !== '---' && pos.position.actualRealizedFundings !== 'N/A' && parseFloat(pos.position.actualRealizedFundings) >= 0 ? '#3fb950' :
                     pos.position.actualRealizedFundings !== '---' && pos.position.actualRealizedFundings !== 'N/A' && parseFloat(pos.position.actualRealizedFundings) < 0 ? '#f85149' : '#ffffff'
            }}>
              {pos.position.actualRealizedFundings !== '---' && pos.position.actualRealizedFundings !== 'N/A' ? formatForDisplay(pos.position.actualRealizedFundings, 2) : pos.position.actualRealizedFundings}
            </td>
          ))}
        </tr>

        {/* Sum row */}
        <tr className="exchange-row diff-sum-row"  style={{ borderBottom: '1px solid #FFFFFF'}}>
          <td className="exchange-cell label"></td>
          <td className="exchange-cell value" colSpan={tradeData.positions.length} style={{textAlign: 'center', color: '#8b949e', fontSize: '11px' }}>
            {(() => {
              const sum = tradeData.positions.reduce((acc, pos) => {
                const value = pos.position.actualRealizedFundings;
                if (value !== '---' && value !== 'N/A') {
                  return acc + parseFloat(value);
                }
                return acc;
              }, 0);
              const hasValidValues = tradeData.positions.some(pos => pos.position.actualRealizedFundings !== '---' && pos.position.actualRealizedFundings !== 'N/A');
              return hasValidValues ? `Sum: ${formatForDisplay(sum, 2)}` : '---';
            })()}
          </td>
        </tr>
        {/* Row: Job Buttons */}
        <tr className="exchange-row" >
          <td className="exchange-cell label" style={{ paddingTop: '3px', color: '#FFFFFF'}}>Position Jobs:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`job-btn-${index}`} className="exchange-cell value" style={{ paddingTop: '3px', paddingRight: '5px'}}>
              <button
                className="job-btn"
                onClick={() => handleJobButtonClick(index)}
                style={{
                  background: '#000000',
                  border: '1px solid #1e90ff',
                  borderRadius: '2px',
                  color: '#1e90ff',
                  padding: '2px 6px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '11px',
                  cursor: 'pointer',
                  minWidth: '40px',
                  transition: 'all 0.2s',
                  lineHeight: '1'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#0a0a0a';
                  e.target.style.color = '#4169e1';
                  e.target.style.borderColor = '#4169e1';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#000000';
                  e.target.style.color = '#1e90ff';
                  e.target.style.borderColor = '#1e90ff';
                }}
              >
                New Job
              </button>
            </td>
          ))}
        </tr>
        {/* Row(s): Related Position Jobs Section */}
        <tr className="exchange-row">
          <td className="exchange-cell label">
            <span className="hide-mobile">Related position JOBs:</span>
            <span className="show-mobile" style={{ display: 'none' }}>Related JOBs:</span>
          </td>
          {tradeData.positions.map((position, posIndex) => (
            <td key={`position-jobs-${posIndex}`} className="exchange-cell value">
              {(() => {
                const positionJobsFiltered = positionJobs.filter(job => job.target_item_id === position?.positionId);
                return positionJobsFiltered.length === 0 ? (
                  '---'
                ) : (
                  <div>
                    {positionJobsFiltered.map((job, index) => (
                      <div
                        key={index}
                        style={{ color: '#8b949e', fontSize: '11px', cursor: 'pointer' }}
                        onClick={(e) => handleJobClick(e, job)}
                        onMouseEnter={(e) => e.target.style.color = 'white'}
                        onMouseLeave={(e) => e.target.style.color = '#8b949e'}
                      >
                        ID: {job.job_id} | {job.status}<br/>{job.action_type}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </td>
          ))}
        </tr>

        {/* Empty row */}
        <tr className="exchange-row" style={{height: '8px'}}>
          <td className="exchange-cell"></td>
          {tradeData.positions.map((pos, index) => (
            <td key={`empty-funding-${index}`} className="exchange-cell"></td>
          ))}
        </tr>

        {/* Assigned fundings header */}
        <tr className="exchange-row" style={{ borderTop: '1px solid #FFFFFF'}}>
          <td className="exchange-cell label" style={{color: '#ffffff',paddingTop: '3px', paddingBottom: '3px'}}>Assigned fundings:</td>
          {tradeData.positions.map((pos, index) => (
            <td key={`funding-header-${index}`} className="exchange-cell value"></td>
          ))}
        </tr>

        {/* Funding rows */}
        {fundings.length === 0 ? (
          <>
            <tr className="exchange-row">
              <td className="exchange-cell" colSpan={tradeData.positions.length + 1} style={{ textAlign: 'center', color: '#8b949e' }}>No funding data available</td>
            </tr>
            <tr className="exchange-row">
              <td className="exchange-cell" colSpan={tradeData.positions.length + 1} style={{ height: '10px' }}></td>
            </tr>
            <tr className="exchange-row">
              <td className="exchange-cell" colSpan={tradeData.positions.length + 1} style={{ height: '10px' }}></td>
            </tr>
          </>
        ) : (
          fundings.map((funding, index) => {
            const dateObj = new Date(funding.timestamp);
            const dateStr = dateObj.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
            const timeStr = dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

            return (
              <tr className="exchange-row" key={index}>
                <td className="exchange-cell label" style={{ lineHeight: '1.0', padding: '0px 0px 0px 0px' }}>
                  <div style={{ fontSize: '11px' }}>
                    {dateStr}
                  </div>
                  <div style={{ fontSize: '11px' }}>
                    {timeStr}
                  </div>
                </td>
                {tradeData.positions.map((pos, posIndex) => {
                  // Map position index to funding field (ex1_funding, ex2_funding, ex3_funding, etc.)
                  const fundingValue = funding[`ex${posIndex + 1}_funding`];
                  const fundingRate = funding[`ex${posIndex + 1}_funding_rate`];

                  return (
                    <td key={`funding-${index}-${posIndex}`} className="exchange-cell value">
                      <div style={{ fontSize: '11px' }}>
                        {fundingValue !== null && fundingValue !== undefined ? formatForDisplay(fundingValue) : '---'}
                      </div>
                      <div style={{ fontSize: '11px' }}>
                        {fundingRate !== null && fundingRate !== undefined ? `${(parseFloat(fundingRate) * 100).toFixed(4)}%` : '---'}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })
        )}

        {/* Empty lines after funding rows */}
        {fundings.length > 0 && (
          <>
            <tr className="exchange-row">
              <td className="exchange-cell" colSpan={tradeData.positions.length + 1} style={{ height: '10px' }}></td>
            </tr>
            <tr className="exchange-row">
              <td className="exchange-cell" colSpan={tradeData.positions.length + 1} style={{ height: '10px' }}></td>
            </tr>
          </>
        )}

        {/* Orderbook performance collapsible section */}
        <tr
          className="exchange-row"
          onClick={() => setShowOrderbookStats(s => !s)}
          style={{ cursor: 'pointer', userSelect: 'none', borderTop: '1px solid #FFFFFF' }}
        >
          <td className="exchange-cell label">
            {showOrderbookStats ? '\u25BC' : '\u25BA'} Orderbook performance:
            {tradeData.positions.map((pos, index) => {
              const baseSymbol = (pos.symbol || '').toUpperCase();
              const statsSymbol = baseSymbol.endsWith('USDT') ? baseSymbol : baseSymbol + 'USDT';
              const contractType = (pos.contract || '').toUpperCase() === 'SPOT' ? 'spot' : 'perpetual';
              const chartUrl = `/chart?exchange=${encodeURIComponent(pos.name)}&symbol=${encodeURIComponent(statsSymbol)}&contractType=${contractType}`;
              return (
                <a
                  key={`chart-link-${index}`}
                  href={chartUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ marginLeft: '8px', fontSize: '11px', color: '#58a6ff', textDecoration: 'none', whiteSpace: 'nowrap' }}
                >
                  {pos.name} ↗
                </a>
              );
            })}
          </td>
          {tradeData.positions.map((pos, index) => (
            <td key={`ob-perf-header-${index}`} className="exchange-cell value">
              {showOrderbookStats && (
                <>
                  <div>
                    <span style={{ display: 'inline-block', minWidth: '70px', textAlign: 'right' }}>quant</span>
                    <span style={{ display: 'inline-block', minWidth: '80px', textAlign: 'right' }}>volume</span>
                  </div>
                  <div>
                    <span style={{ display: 'inline-block', minWidth: '70px', textAlign: 'right' }}>qty/s</span>
                    <span style={{ display: 'inline-block', minWidth: '80px', textAlign: 'right' }}>vol/s</span>
                  </div>
                  <div>
                    <span style={{ display: 'inline-block', minWidth: '70px', textAlign: 'right' }}>sprd%</span>
                    <span style={{ display: 'inline-block', minWidth: '80px', textAlign: 'right' }}>vwap</span>
                  </div>
                </>
              )}
            </td>
          ))}
        </tr>

        {showOrderbookStats && [
          { key: '1s',  label: '1sec:'  },
          { key: '5s',  label: '5sec:'  },
          { key: '10s', label: '10sec:' },
          { key: '1m',  label: '1min:'  },
          { key: '10m', label: '10min:' }
        ].map(({ key, label }) => (
          <tr key={`ob-perf-${key}`} className="exchange-row" style={{ borderBottom: '1px solid #3a3a3a' }}>
            <td className="exchange-cell label" style={{ paddingLeft: '16px', verticalAlign: 'top' }}>{label}</td>
            {tradeData.positions.map((pos, index) => {
              const w = orderbookStats[index]?.[key];
              const spreadPct = w && w.min_price && w.max_price && w.min_price > 0
                ? (w.max_price - w.min_price) / w.min_price * 100
                : null;
              return (
                <td key={`ob-perf-${key}-${index}`} className="exchange-cell value">
                  <div>
                    <span style={{ display: 'inline-block', minWidth: '70px', textAlign: 'right' }}>
                      {w ? formatForDisplay(w.total_quantity, getOptimalDecimalPlaces(7, w.total_quantity, 4)) : '---'}
                    </span>
                    <span style={{ display: 'inline-block', minWidth: '80px', textAlign: 'right' }}>
                      {w ? formatForDisplay(w.total_volume, getOptimalDecimalPlaces(7, w.total_volume, 4)) : '---'}
                    </span>
                  </div>
                  <div>
                    <span style={{ display: 'inline-block', minWidth: '70px', textAlign: 'right' }}>
                      {w ? formatForDisplay(w.quantity_per_second, getOptimalDecimalPlaces(7, w.quantity_per_second, 4)) : '---'}
                    </span>
                    <span style={{ display: 'inline-block', minWidth: '80px', textAlign: 'right' }}>
                      {w ? formatForDisplay(w.volume_per_second, getOptimalDecimalPlaces(7, w.volume_per_second, 4)) : '---'}
                    </span>
                  </div>
                  <div>
                    <span style={{ display: 'inline-block', minWidth: '70px', textAlign: 'right' }}>
                      {spreadPct !== null ? formatForDisplay(spreadPct, getOptimalDecimalPlaces(6, spreadPct, 4)) : '---'}
                    </span>
                    <span style={{ display: 'inline-block', minWidth: '80px', textAlign: 'right' }}>
                      {w && w.vwap ? formatForDisplay(w.vwap, getOptimalDecimalPlaces(7, w.vwap, 4)) : '---'}
                    </span>
                  </div>
                </td>
              );
            })}
          </tr>
        ))}

      </tbody>
      </table>

      {/* Job Dialog */}
      {showJobDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: '6px',
            padding: '20px',
            minWidth: '400px',
            maxWidth: '500px',
            fontFamily: "'Courier New', Courier, monospace",
            color: '#c9d1d9'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '14px',
              fontWeight: 'normal',
              color: '#ffffff',
              borderBottom: '1px solid #30363d',
              paddingBottom: '8px'
            }}>
              Schedule Job
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Position ID:
              </label>
              <input
                type="text"
                value={jobDialogData.positionId}
                disabled
                style={{
                  width: '100%',
                  background: '#161b22',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#6e7681',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Action Type:
              </label>
              <select
                value={jobDialogData.actionType}
                onChange={(e) => handleJobDialogChange('actionType', e.target.value)}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <option value="OPEN">OPEN</option>
                <option value="CLOSE">CLOSE</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Trigger Type:
              </label>
              <select
                value={jobDialogData.triggerType}
                onChange={(e) => handleJobDialogChange('triggerType', e.target.value)}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <option value="funding_rate">funding_rate</option>
                <option value="IF-PRICE">IF-PRICE</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Condition:
              </label>
              <select
                value={jobDialogData.condition}
                onChange={(e) => handleJobDialogChange('condition', e.target.value)}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <option value=">">{'>'}</option>
                <option value=">=">{'>='}</option>
                <option value="<">{'<'}</option>
                <option value="<=">{'<='}</option>
              </select>
            </div>


            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Condition value:
              </label>
              <input
                type="text"
                value={jobDialogData.conditionValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                    handleJobDialogChange('conditionValue', value);
                  }
                }}
                placeholder="0.0000"
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px'
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Volume:
              </label>
              <input
                type="text"
                value={jobDialogData.volume}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    handleJobDialogChange('volume', value);
                  }
                }}
                placeholder="0.00"
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '4px' }}>
                <label style={{ color: '#8b949e', fontSize: '12px' }}>
                  Scheduled Time:
                </label>
                <span
                  onClick={() => {
                    const now = new Date();
                    const pad = n => String(n).padStart(2, '0');
                    const value = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                    handleJobDialogChange('scheduledTime', value);
                  }}
                  style={{ color: '#8b949e', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', marginLeft: '8px' }}
                  onMouseEnter={e => e.target.style.color = '#c9d1d9'}
                  onMouseLeave={e => e.target.style.color = '#8b949e'}
                >
                  Set now
                </span>
              </div>
              <input
                type="datetime-local"
                value={jobDialogData.scheduledTime}
                onChange={(e) => handleJobDialogChange('scheduledTime', e.target.value)}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleJobDialogClose}
                style={{
                  padding: '6px 16px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#8b949e',
                  fontSize: '12px',
                  fontFamily: "'Courier New', Courier, monospace",
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#30363d';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#21262d';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleJobDialogSubmit}
                style={{
                  padding: '6px 16px',
                  background: '#238636',
                  border: '1px solid #238636',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontFamily: "'Courier New', Courier, monospace",
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#2ea043';
                  e.target.style.borderColor = '#2ea043';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#238636';
                  e.target.style.borderColor = '#238636';
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Details Popup */}
      {jobDetailsPopup.show && jobDetailsPopup.job && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'transparent',
              zIndex: 998
            }}
            onClick={closeJobDetailsPopup}
          />
          <div
            style={{
              position: 'fixed',
              left: jobDetailsPopup.position.x,
              top: jobDetailsPopup.position.y,
              backgroundColor: '#1a1b23',
              border: '1px solid #2a2d3a',
              borderRadius: '4px',
              padding: '12px',
              zIndex: 999,
              minWidth: '250px',
              maxWidth: '350px',
              fontSize: '11px',
              fontFamily: "'Courier New', Courier, monospace",
              color: '#c9d1d9',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold', marginBottom: '6px', fontSize: '12px', color: '#ffffff', borderBottom: '1px solid #2a2d3a', paddingBottom: '4px' }}>
                <span>Job Details</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteJob(jobDetailsPopup.job.job_id);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8b949e',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#f85149'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#8b949e'}
                  title="Delete job"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M11 1.75V3h2.25a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1 0-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675l.66 6.6a.25.25 0 0 0 .249.225h5.19a.25.25 0 0 0 .249-.225l.66-6.6a.75.75 0 0 1 1.492.149l-.66 6.6A1.748 1.748 0 0 1 10.595 15h-5.19a1.75 1.75 0 0 1-1.741-1.575l-.66-6.6a.75.75 0 1 1 1.492-.15zM6.5 1.75V3h3V1.75a.25.25 0 0 0-.25-.25h-2.5a.25.25 0 0 0-.25.25z"></path>
                  </svg>
                </button>
              </div>
              <div><span style={{ color: '#8b949e' }}>Job ID:</span> {jobDetailsPopup.job.job_id}</div>
              <div><span style={{ color: '#8b949e' }}>Target Item ID:</span> {jobDetailsPopup.job.target_item_id}</div>
              <div><span style={{ color: '#8b949e' }}>Status:</span> {jobDetailsPopup.job.status}</div>
              <div><span style={{ color: '#8b949e' }}>Action Type:</span> {jobDetailsPopup.job.action_type}</div>
              <div><span style={{ color: '#8b949e' }}>Condition:</span> {jobDetailsPopup.job.condition}</div>
              <div><span style={{ color: '#8b949e' }}>Volume:</span> {jobDetailsPopup.job.volume}</div>
              <div><span style={{ color: '#8b949e' }}>Scheduled Time:</span> {jobDetailsPopup.job.scheduled_time ? new Date(jobDetailsPopup.job.scheduled_time).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : '-'}</div>
              {jobDetailsPopup.job.created_at_timestamp && (
                <div><span style={{ color: '#8b949e' }}>Created At:</span> {new Date(jobDetailsPopup.job.created_at_timestamp).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</div>
              )}
              {jobDetailsPopup.job.updated_at_timestamp && (
                <div><span style={{ color: '#8b949e' }}>Updated At:</span> {new Date(jobDetailsPopup.job.updated_at_timestamp).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</div>
              )}
              {jobDetailsPopup.job.executed_at_timestamp && (
                <div><span style={{ color: '#8b949e' }}>Executed At:</span> {new Date(jobDetailsPopup.job.executed_at_timestamp).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</div>
              )}
              {jobDetailsPopup.job.done_at_timestamp && (
                <div><span style={{ color: '#8b949e' }}>Finished At:</span> {new Date(jobDetailsPopup.job.done_at_timestamp).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</div>
              )}
              <div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                <span style={{ color: '#8b949e' }}>Log:</span> {jobDetailsPopup.job.log || '-'}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Trade Job Dialog */}
      {showTradeJobDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: '6px',
            padding: '20px',
            minWidth: '400px',
            maxWidth: '500px',
            fontFamily: "'Courier New', Courier, monospace",
            color: '#c9d1d9'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '14px',
              fontWeight: 'normal',
              color: '#ffffff',
              borderBottom: '1px solid #30363d',
              paddingBottom: '8px'
            }}>
              Schedule Trade Job
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Trade ID:
              </label>
              <input
                type="text"
                value={tradeJobDialogData.tradeId}
                disabled
                style={{
                  width: '100%',
                  background: '#161b22',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#6e7681',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Action Type:
              </label>
              <select
                value={tradeJobDialogData.actionType}
                onChange={(e) => handleTradeJobDialogChange('actionType', e.target.value)}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <option value="OPEN-TRADE">OPEN-TRADE</option>
                <option value="CLOSE-TRADE">CLOSE-TRADE</option>
                <option value="FORCED-CLOSE-TRADE">FORCED-CLOSE-TRADE</option>
                <option value="CLOSE-AUTOMATIC">CLOSE-AUTOMATIC</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Trigger Type:
              </label>
              <select
                value={tradeJobDialogData.triggerType}
                onChange={(e) => handleTradeJobDialogChange('triggerType', e.target.value)}
                disabled={tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE'}
                style={{
                  width: '100%',
                  background: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#161b22' : '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#6e7681' : '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="TRADE_PROFIT">TRADE_PROFIT</option>
                <option value="TIME">TIME</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Condition:
              </label>
              <select
                value={tradeJobDialogData.condition}
                onChange={(e) => handleTradeJobDialogChange('condition', e.target.value)}
                disabled={tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE'}
                style={{
                  width: '100%',
                  background: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#161b22' : '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#6e7681' : '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? 'not-allowed' : 'pointer'
                }}
              >
                <option value=">">{'>'}</option>
                <option value=">=">{'>='}</option>
                <option value="<">{'<'}</option>
                <option value="<=">{'<='}</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Trade profit [%]:
              </label>
              <input
                type="text"
                value={tradeJobDialogData.tradeProfit}
                onChange={(e) => handleTradeJobDialogChange('tradeProfit', e.target.value)}
                disabled={tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE'}
                placeholder="0.0000"
                style={{
                  width: '100%',
                  background: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#161b22' : '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#6e7681' : '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? 'not-allowed' : 'text'
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Volume:
              </label>
              <input
                type="text"
                value={tradeJobDialogData.volume}
                onChange={(e) => handleTradeJobDialogChange('volume', e.target.value)}
                disabled={tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE'}
                placeholder="0.00"
                style={{
                  width: '100%',
                  background: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#161b22' : '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#6e7681' : '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: tradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? 'not-allowed' : 'text'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '4px' }}>
                <label style={{ color: '#8b949e', fontSize: '12px' }}>
                  Scheduled Time:
                </label>
                <span
                  onClick={() => {
                    const now = new Date();
                    const pad = n => String(n).padStart(2, '0');
                    const value = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                    handleTradeJobDialogChange('scheduledTime', value);
                  }}
                  style={{ color: '#8b949e', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', marginLeft: '8px' }}
                  onMouseEnter={e => e.target.style.color = '#c9d1d9'}
                  onMouseLeave={e => e.target.style.color = '#8b949e'}
                >
                  Set now
                </span>
              </div>
              <input
                type="datetime-local"
                value={tradeJobDialogData.scheduledTime}
                onChange={(e) => handleTradeJobDialogChange('scheduledTime', e.target.value)}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'text'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTradeJobDialog(false)}
                style={{
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontFamily: "'Courier New', Courier, monospace",
                  backgroundColor: '#21262d',
                  color: '#c9d1d9',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#30363d';
                  e.target.style.borderColor = '#8b949e';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#21262d';
                  e.target.style.borderColor = '#30363d';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTradeJob}
                style={{
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontFamily: "'Courier New', Courier, monospace",
                  backgroundColor: '#238636',
                  color: '#ffffff',
                  border: '1px solid #238636',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2ea043';
                  e.target.style.borderColor = '#2ea043';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#238636';
                  e.target.style.borderColor = '#238636';
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Position Dialog */}
      <AddPositionDialog
        show={showAddPositionDialog}
        onClose={() => setShowAddPositionDialog(false)}
        tradeId={tradeId}
        symbolId={symbolId}
        symbolName={tradeData.positions[0]?.symbol || ''}
        onPositionAdded={handlePositionAdded}
      />
    </div>
  );
};

export default TradeDetailWindow;
